import { getPrinterBridge, type PrintOutputOptions } from './printRules'
import { renderTreatmentDocument } from './treatmentPrint'
import { renderDrugReturnReceipt } from './pharmacyReturnPrint'
import { getRefundPaymentRecords, renderChargeRefundReceipt } from './chargeRefundPrint'
import { readChinesePrescriptionNumber, formatChinesePrescriptionNumber, isPrescriptionDocumentTitleVisible } from './prescriptionHeaderSettings'
import { summarizePrescriptionCharges } from './prescriptionAmount'
import { ElMessage } from 'element-plus'
import {
  printRestoreDetail,
  printDrugDetail,
  printPatientDetail,
  printDispensingDetail,
  printPrescriptionDetail,
  printTreatmentDetail,
  printMedicalDetail,
  printRegistrationReceiptDetail,
  printDiagnosisCertificateDetail,
} from '@/api/manage/printSetting'
import { dispensingDetail as getDispensingSettingDetail } from '@/api/manage/dispensing'
import {
  getChargeReceiptPrint,
  getMedicationInstructionPrint,
  getMedicationLabelPrint,
  getRefundReceiptPrint,
  getWithdrawalDrugReceiptPrint,
  getPatientLabelPrint,
  getDispensingReceiptPrint,
  getRegistrationReceiptPrint,
  getMedicalRecordPrint,
  getPrescriptionPrint,
  getTreatmentPrint,
  getDiagnosisCertificatePrint,
} from '@/api/binglilog'
import {
  getPatientCardById,
  getPatientLevelById,
  getChargeDetail,
} from '@/api/charge/charge'
import { patientCardDetail } from '@/api/patient/card'
import { custFormatDate } from '@/libs/utils/data-jsFn'
import { getEnabledPrescriptionSignatureLines } from '@/libs/utils/prescriptionSignatureLine'
import { executionDetail as getExecutionDetail } from '@/api/execute/index'
import { caseItemSwitchGet } from '@/api/outpatients/inquiryDetail'
import { getDrugDeviceCatalogList } from '@/api/inventory/yaoxie'
import { DICT_TYPE, getStrDictOptions, getDictLabel } from '@/libs/utils/dict'
export const hasMedicationNotes = (notes: unknown): boolean =>
  typeof notes === 'string' && Boolean(notes.replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|&#160;|&#xA0;/gi, ' ').trim())

// 详情中任一处方有说明即可打印，空说明不影响其他处方。
export const canPrintMedicationInstructions = (notes: unknown[]): boolean =>
  notes.some(hasMedicationNotes)

const getPrintFailureMessage = (error: any) =>
  error?.response?.data?.msg || error?.msg || error?.message ||
  (typeof error === 'string' ? error : '打印失败')

const escapePrintHtml = (value: any) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')

const formatDiagnosisCertificateAge = (detail: any) => {
  const parts = [
    detail.ageYear ? `${detail.ageYear}岁` : '',
    detail.ageMonth ? `${detail.ageMonth}月` : '',
    detail.ageDay ? `${detail.ageDay}天` : '',
  ].filter(Boolean)
  return parts.join('')
}

// 金额格式化
const formatMoney = (value: any, digits = 2) => {
  const num = Number(value ?? 0)
  if (!Number.isFinite(num)) return '0'
  return parseFloat(num.toFixed(digits)).toString()
}

const hasNumericValue = (value: any) => {
  return value !== '' && value !== null && value !== undefined
    ? Number.isFinite(Number(value))
    : false
}

const pickNumericValue = (...values: any[]) => {
  for (const value of values) {
    if (hasNumericValue(value)) {
      return Number(value)
    }
  }
  return null
}

const getNumericValue = (...values: any[]) => {
  const value = pickNumericValue(...values)
  return value === null ? 0 : value
}

const getAmountValue = (...values: any[]) => {
  for (const value of values) {
    if (hasNumericValue(value) && Number(value) > 0) {
      return Number(value)
    }
  }
  return getNumericValue(...values)
}

const fillChineseMedicineCabinetNumbers = async (prescriptionList: any[]) => {
  const chineseDrugList = (prescriptionList || [])
    .filter((prescription: any) => prescription?.prescriptionType === 1)
    .flatMap((prescription: any) =>
      Array.isArray(prescription?.drugList) ? prescription.drugList : []
    )
  const drugIds = Array.from(
    new Set(
      chineseDrugList
        .filter((drug: any) => drug?.drugId && !drug?.cabinetNumber)
        .map((drug: any) => drug.drugId)
    )
  )

  if (!drugIds.length) return

  try {
    const res = await getDrugDeviceCatalogList({ ids: drugIds })
    if (res.code !== 0 || !Array.isArray(res.data)) return

    const catalogMap = new Map(res.data.map((item: any) => [item.id, item]))
    chineseDrugList.forEach((drug: any) => {
      const cabinetNumber = catalogMap.get(drug.drugId)?.cabinetNumber
      if (cabinetNumber) {
        drug.cabinetNumber = cabinetNumber
      }
    })
  } catch (error) {
    console.error('获取药品柜号失败:', error)
  }
}

const formatWeight = (value: number, digits = 2) => {
  if (!Number.isFinite(value)) return ''
  return parseFloat(value.toFixed(digits)).toString()
}

const joinDisplayParts = (parts: any[], separator = '，') => {
  return parts
    .map(item =>
      item === null || item === undefined ? '' : String(item).trim()
    )
    .filter(Boolean)
    .join(separator)
}

const formatChineseMedicineUsageDetail = (prescription: any) => {
  const frequencyText = joinDisplayParts(
    [
      prescription?.frequency,
      prescription?.frequencyUnit,
      prescription?.dosage,
    ],
    ' '
  )
  return joinDisplayParts([prescription?.usageMethod, frequencyText])
}

const formatChineseMedicineDoseUsage = (prescription: any) => {
  return joinDisplayParts([
    `共${prescription?.quantity || 0}剂`,
    formatChineseMedicineUsageDetail(prescription),
  ])
}

const getChineseMedicineSingleDoseWeight = (
  drug: any,
  prescription: any
): string => {
  const unit = drug?.unit || 'g'
  const explicitDoseWeight = pickNumericValue(
    drug?.dosage,
    drug?.dosagePerTime,
    drug?.singleDoseWeight
  )

  if (explicitDoseWeight !== null && explicitDoseWeight > 0) {
    return `${formatWeight(explicitDoseWeight)}${unit}`
  }

  const totalWeight = pickNumericValue(drug?.quantity, drug?.weight)
  if (totalWeight === null || totalWeight <= 0) return ''

  const doseCount = Math.max(getNumericValue(prescription?.quantity, 1), 1)
  return `${formatWeight(totalWeight / doseCount)}${unit}`
}

const formatWesternMedicineUsageDosage = (drug: any) => {
  const dosageText =
    drug?.dosage === null || drug?.dosage === undefined
      ? ''
      : String(drug.dosage).trim()
  const dosagePerTime = pickNumericValue(drug?.dosagePerTime)

  if (dosagePerTime !== null && dosagePerTime > 0) {
    const unit =
      drug?.minPackageUnit ||
      (dosageText && !/\d/.test(dosageText) ? dosageText : '')
    if (unit) return `每次${formatWeight(dosagePerTime)}${unit}`
    if (!dosageText) return `每次${formatWeight(dosagePerTime)}`
  }

  return dosageText
}

const formatWesternMedicineUsage = (drug: any) => {
  const dosage = formatWesternMedicineUsageDosage(drug)

  return [dosage, drug?.frequencyUnit || drug?.frequency, drug?.usageMethod]
    .filter(item => item !== undefined && item !== null && String(item).trim())
    .join('，')
}

const getMergedProcessingList = (detailData: any) => {
  const directProcessList = Array.isArray(detailData?.processList)
    ? detailData.processList.filter(Boolean)
    : []

  const nestedProcessList = Array.isArray(detailData?.prescriptionList)
    ? detailData.prescriptionList
        .filter(
          (prescription: any) =>
            prescription?.prescriptionType === 1 && prescription?.processing
        )
        .map((prescription: any) => ({
          ...prescription.processing,
          prescriptionsId:
            prescription.processing?.prescriptionsId ??
            prescription.processing?.prescriptionId ??
            prescription?.id ??
            prescription?.prescriptionId,
        }))
    : []

  if (directProcessList.length === 0) return nestedProcessList

  const buildProcessKey = (item: any) =>
    String(
      item?.id ??
        item?.prescriptionsId ??
        item?.prescriptionId ??
        item?.processingId ??
        `${item?.processingName || ''}-${
          item?.totalPrice || item?.payPrice || 0
        }`
    )

  const seenKeys = new Set(
    directProcessList.map((item: any) => buildProcessKey(item))
  )
  const mergedProcessList = [...directProcessList]

  nestedProcessList.forEach((item: any) => {
    const key = buildProcessKey(item)
    if (!seenKeys.has(key)) {
      seenKeys.add(key)
      mergedProcessList.push(item)
    }
  })

  return mergedProcessList
}

const getChargeRecordItems = (record: any) => {
  if (Array.isArray(record?.itemList) && record.itemList.length > 0) {
    return record.itemList
  }
  if (Array.isArray(record?.drugList) && record.drugList.length > 0) {
    return record.drugList
  }
  return []
}

const getChargeDiscountAmount = (data: any) => {
  const directDiscount = pickNumericValue(
    data?.totalDiscount,
    data?.discountPrice
  )
  if (directDiscount !== null) {
    return directDiscount
  }
  return (
    getNumericValue(data?.levelDiscount) +
    getNumericValue(data?.cardDiscount) +
    getNumericValue(data?.negotiatedDiscount) +
    getNumericValue(data?.dicker)
  )
}

const getChargeItemQuantity = (item: any) =>
  getNumericValue(item?.quantity, item?.refundNum, 1)

const getChargeItemTotalAmount = (item: any) => {
  const explicitAmount = pickNumericValue(item?.totalPrice, item?.amount)
  if (explicitAmount !== null) {
    return explicitAmount
  }

  const unitPrice = pickNumericValue(item?.unitPrice, item?.singlePrice)
  if (unitPrice !== null) {
    return Number((unitPrice * getChargeItemQuantity(item)).toFixed(2))
  }

  return getNumericValue(item?.price, item?.payPrice, item?.payAmount)
}

const getChargeItemReceivableAmount = (item: any) => {
  const explicitAmount = pickNumericValue(
    item?.payPrice,
    item?.payAmount,
    item?.price
  )
  if (explicitAmount !== null) {
    return explicitAmount
  }
  return Math.max(
    getChargeItemTotalAmount(item) - getChargeDiscountAmount(item),
    0
  )
}

const getChargeRecordTotalAmount = (record: any) => {
  const explicitAmount = pickNumericValue(record?.totalPrice, record?.amount)
  if (explicitAmount !== null) {
    return explicitAmount
  }

  const items = getChargeRecordItems(record)
  if (items.length > 0) {
    return items.reduce(
      (sum: number, item: any) => sum + getChargeItemTotalAmount(item),
      0
    )
  }
  return getNumericValue(record?.price)
}

const getChargeRecordReceivableAmount = (record: any) => {
  const explicitAmount = pickNumericValue(
    record?.payPrice,
    record?.payAmount,
    record?.price
  )
  if (explicitAmount !== null) {
    return explicitAmount
  }

  const items = getChargeRecordItems(record)
  if (items.length > 0) {
    return items.reduce(
      (sum: number, item: any) => sum + getChargeItemReceivableAmount(item),
      0
    )
  }

  return Math.max(
    getChargeRecordTotalAmount(record) - getChargeDiscountAmount(record),
    0
  )
}

const getItemListAmount = (list: any[] = []) => {
  return list.reduce((sum: number, item: any) => {
    const childDrugTotal = Array.isArray(item?.drugList)
      ? item.drugList.reduce(
          (drugSum: number, drug: any) =>
            drugSum + getAmountValue(drug?.totalPrice, drug?.payPrice),
          0
        )
      : 0
    const itemAmount = getAmountValue(item?.totalPrice, item?.payPrice)
    const calculatedAmount =
      getNumericValue(item?.unitPrice) * getNumericValue(item?.quantity)

    return (
      sum +
      (childDrugTotal > 0
        ? childDrugTotal
        : itemAmount > 0
        ? itemAmount
        : calculatedAmount)
    )
  }, 0)
}

const getPrescriptionAmount = (prescription: any) => {
  if (Number(prescription?.prescriptionType) === 3) {
    const itemListAmount = Array.isArray(prescription?.itemList)
      ? getItemListAmount(prescription.itemList)
      : 0
    if (itemListAmount > 0) return itemListAmount

    const drugListAmount = Array.isArray(prescription?.drugList)
      ? getItemListAmount([{ drugList: prescription.drugList }])
      : 0
    if (drugListAmount > 0) return drugListAmount

    const calculatedAmount =
      getNumericValue(prescription?.unitPrice) *
      getNumericValue(prescription?.quantity)
    if (calculatedAmount > 0) return calculatedAmount
  }

  return getAmountValue(prescription?.totalPrice, prescription?.payPrice)
}

const normalizeReceiptSelection = (value: any) =>
  Array.isArray(value) ? value : []

const normalizePrintSelection = (value: any, fallback: string[] = []) => {
  if (Array.isArray(value)) return value

  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value)
      if (Array.isArray(parsed)) return parsed
    } catch (error) {
      return value
        .split(',')
        .map(item => item.trim())
        .filter(Boolean)
    }
  }

  return fallback
}

const escapeHtml = (value: any) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const getMemberBalance = (memberDetail: any) =>
  Number(
    (
      getNumericValue(memberDetail?.balance) +
      getNumericValue(memberDetail?.giftBalance)
    ).toFixed(2)
  )

const buildCardRightsList = (cardDetailData: any) => {
  const cardList = Array.isArray(cardDetailData?.cardList)
    ? cardDetailData.cardList
    : []

  return cardList
    .map((item: any) => {
      const remainingCount =
        getNumericValue(item?.count, item?.chooseCount) -
        getNumericValue(item?.usedCount)
      return {
        projectName: item?.projectName || item?.name || '',
        remainingCount,
      }
    })
    .filter(
      (item: any) => item.projectName && Number(item.remainingCount || 0) > 0
    )
}

const formatPrintDateTime = (value: any) => {
  if (!value) return new Date().toLocaleString('zh-CN')
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString('zh-CN')
}

const buildChargeItemKey = (item: any, record: any) => {
  return [
    item?.serviceType ?? record?.chargeType ?? '',
    item?.drugId ?? item?.serviceId ?? '',
    item?.drugName ||
      item?.name ||
      item?.serviceName ||
      item?.itemName ||
      record?.name ||
      '收费项目',
    getNumericValue(item?.unitPrice, item?.singlePrice, record?.unitPrice),
    item?.unit || record?.unit || '',
  ].join('|')
}

const buildMedicalChargePrintItems = (patientData: any) => {
  const items: Array<{ name: string; quantity: number; amount: number }> = []

  if (hasNumericValue(patientData?.registerPrice)) {
    items.push({
      name: '挂号费',
      quantity: 1,
      amount: Number(patientData.registerPrice),
    })
  }

  const prescriptionList = Array.isArray(patientData?.prescriptionList)
    ? patientData.prescriptionList
    : []

  prescriptionList.forEach((record: any) => {
    const recordItems = getChargeRecordItems(record)
    if (recordItems.length > 0) {
      recordItems.forEach((item: any) => {
        if (
          item?.tollStatus !== true ||
          !item?.drugName ||
          !hasNumericValue(item?.quantity) ||
          !hasNumericValue(item?.totalPrice)
        ) {
          return
        }

        items.push({
          name: item.drugName,
          quantity: Number(item.quantity),
          amount: Number(item.totalPrice),
        })
      })
      return
    }

    if (
      record?.tollStatus === true &&
      record?.name &&
      hasNumericValue(record?.quantity) &&
      hasNumericValue(record?.totalPrice)
    ) {
      items.push({
        name: record.name,
        quantity: Number(record.quantity),
        amount: Number(record.totalPrice),
      })
    }
  })

  return items
}

const updateChargeSummaryItems = (
  itemMap: Map<string, { name: string; quantity: number; amount: number }>,
  key: string,
  item: { name: string; quantity: number; amount: number },
  sign: number
) => {
  const current = itemMap.get(key) || {
    name: item.name,
    quantity: 0,
    amount: 0,
  }

  current.quantity = Number(
    (current.quantity + item.quantity * sign).toFixed(3)
  )
  current.amount = Number((current.amount + item.amount * sign).toFixed(2))
  itemMap.set(key, current)
}

const buildChargePrintSummary = (
  patientData: any,
  chargeRecords: any[],
  refundRecords: any[] = []
) => {
  const summary = {
    items: [] as Array<{ name: string; quantity: number; amount: number }>,
    totalAmount: 0,
    discountAmount: 0,
    receivableAmount: 0,
    actualPaidAmount: 0,
    chargeUserName: patientData?.chargeUserName || '',
    chargeTime: patientData?.chargeDate || '',
    remark: patientData?.remark || '',
  }
  const itemMap = new Map<
    string,
    { name: string; quantity: number; amount: number }
  >()

  let hasRecordLevelReceivable = false
  let hasRecordLevelActualPaid = false
  let hasRecordLevelDiscount = false
  let latestChargeTime = 0

  const applyRecords = (
    records: any[],
    sign: number,
    useChargeMeta = false
  ) => {
    ;(records || []).forEach((record: any) => {
      const items = getChargeRecordItems(record)
      const recordTotalAmount = getChargeRecordTotalAmount(record)
      const recordReceivableAmount = getChargeRecordReceivableAmount(record)
      let recordDiscountAmount = getChargeDiscountAmount(record)
      if (
        recordDiscountAmount <= 0 &&
        recordTotalAmount > recordReceivableAmount
      ) {
        recordDiscountAmount = Number(
          (recordTotalAmount - recordReceivableAmount).toFixed(2)
        )
      }

      if (items.length > 0) {
        items.forEach((item: any) => {
          let amount = getChargeItemTotalAmount(item)
          if (amount <= 0 && items.length === 1 && recordTotalAmount > 0) {
            amount = recordTotalAmount
          }
          updateChargeSummaryItems(
            itemMap,
            buildChargeItemKey(item, record),
            {
              name:
                item?.drugName ||
                item?.name ||
                item?.serviceName ||
                item?.itemName ||
                record?.name ||
                '收费项目',
              quantity: getChargeItemQuantity(item),
              amount,
            },
            sign
          )
        })
      } else {
        updateChargeSummaryItems(
          itemMap,
          buildChargeItemKey(record, record),
          {
            name: record?.name || record?.drugName || '收费项目',
            quantity: getNumericValue(record?.quantity, 1),
            amount: recordTotalAmount,
          },
          sign
        )
      }

      summary.totalAmount = Number(
        (summary.totalAmount + recordTotalAmount * sign).toFixed(2)
      )
      summary.discountAmount = Number(
        (summary.discountAmount + recordDiscountAmount * sign).toFixed(2)
      )
      summary.receivableAmount = Number(
        (summary.receivableAmount + recordReceivableAmount * sign).toFixed(2)
      )
      hasRecordLevelDiscount = true
      hasRecordLevelReceivable = true

      const actualPaid = pickNumericValue(
        record?.price,
        record?.actualPrice,
        record?.payAmount,
        record?.payPrice
      )
      if (actualPaid !== null) {
        summary.actualPaidAmount = Number(
          (summary.actualPaidAmount + actualPaid * sign).toFixed(2)
        )
        hasRecordLevelActualPaid = true
      }

      if (useChargeMeta) {
        const chargeTime = getNumericValue(record?.createTime)
        if (chargeTime >= latestChargeTime) {
          latestChargeTime = chargeTime
          summary.chargeTime = record?.createTime || summary.chargeTime
          summary.chargeUserName =
            record?.chargeUserName || summary.chargeUserName
          summary.remark = record?.remark || summary.remark
        }
      }
    })
  }

  applyRecords(chargeRecords, 1, true)
  applyRecords(refundRecords, -1, false)

  summary.items = buildMedicalChargePrintItems(patientData)

  if (summary.totalAmount <= 0) {
    summary.totalAmount = getNumericValue(patientData?.totalPrice)
  }

  if (!hasRecordLevelDiscount) {
    summary.discountAmount = getChargeDiscountAmount(patientData)
  }
  if (summary.discountAmount <= 0) {
    summary.discountAmount = Math.max(
      summary.totalAmount - summary.receivableAmount,
      0
    )
  }

  if (!hasRecordLevelReceivable) {
    summary.receivableAmount = getNumericValue(
      patientData?.payPrice,
      patientData?.actualPrice
    )
  }
  if (summary.receivableAmount <= 0 && summary.totalAmount > 0) {
    summary.receivableAmount = Math.max(
      summary.totalAmount - summary.discountAmount,
      0
    )
  }

  if (!hasRecordLevelActualPaid) {
    summary.actualPaidAmount = getNumericValue(
      patientData?.payPrice !== undefined ||
        patientData?.refundPrice !== undefined
        ? getNumericValue(patientData?.payPrice) -
            getNumericValue(patientData?.refundPrice)
        : undefined
    )
  }

  const hasPatientLevelDiscount =
    hasNumericValue(patientData?.levelDiscount) ||
    hasNumericValue(patientData?.cardDiscount) ||
    hasNumericValue(patientData?.negotiatedDiscount) ||
    hasNumericValue(patientData?.dicker) ||
    hasNumericValue(patientData?.totalDiscount) ||
    hasNumericValue(patientData?.discountPrice)

  const patientNetPaid =
    hasNumericValue(patientData?.payPrice) ||
    hasNumericValue(patientData?.refundPrice)
      ? Number(
          (
            getNumericValue(patientData?.payPrice) -
            getNumericValue(patientData?.refundPrice)
          ).toFixed(2)
        )
      : null

  if (patientNetPaid !== null && patientNetPaid >= 0) {
    summary.receivableAmount = patientNetPaid
    summary.actualPaidAmount = patientNetPaid
  }

  if (hasPatientLevelDiscount) {
    summary.discountAmount = Number(
      getChargeDiscountAmount(patientData).toFixed(2)
    )
  }

  if (patientNetPaid !== null && hasPatientLevelDiscount) {
    summary.totalAmount = Number(
      (summary.receivableAmount + summary.discountAmount).toFixed(2)
    )
  }

  summary.totalAmount = Number(summary.totalAmount.toFixed(2))
  summary.discountAmount = Number(
    Math.max(summary.discountAmount, 0).toFixed(2)
  )
  summary.receivableAmount = Number(
    Math.max(summary.receivableAmount, 0).toFixed(2)
  )
  summary.actualPaidAmount = Number(
    Math.max(summary.actualPaidAmount, 0).toFixed(2)
  )

  return summary
}

// 获取纸张尺寸配置
const getPaperSize = (config: any, defaultType: string = 'A4') => {
  // 优先从 paperSize 获取，如果没有则根据 printFormat 判断
  // printFormat: 1-80mm, 2-A5, 3-A4 (根据业务需求扩展)
  const paperType =
    config.paperSize ||
    (config.printFormat === 2
      ? 'A5'
      : config.printFormat === 3
      ? 'A4'
      : config.printFormat === 1
      ? '80mm'
      : defaultType)

  switch (paperType) {
    case '58mm':
      return { width: '58mm', height: 'auto', margin: '0', maxWidth: '100%' }
    case '60mm':
      return { width: '60mm', height: 'auto', margin: '0', maxWidth: '100%' }
    case '80mm':
      return { width: '80mm', height: 'auto', margin: '0', maxWidth: '100%' }
    case 'A4':
      return {
        width: '210mm',
        height: '297mm',
        margin: '10mm',
        maxWidth: '100%',
      }
    case 'A5':
      return {
        width: '148mm',
        height: '210mm',
        margin: '10mm',
        maxWidth: '100%',
      }
    default:
      return { width: '80mm', margin: '0', maxWidth: '100%' }
  }
}

// 获取医院名称
const getHospitalName = (config: any) => {
  if (!config) return ''
  const name = config.hospitalName || config.settingName || ''
  return Array.isArray(name) ? name.join(' ') : name
}

const getPatientVisitNumber = (data: any) => data?.medicalSection || ''

const wait = (ms: number) =>
  new Promise(resolve => {
    setTimeout(resolve, ms)
  })

const LINKED_PRINT_DELAY = 800
const PATIENT_LABEL_FEED_SPACE = '40mm'

// 执行打印
const executePrint = async (
  html: string,
  paperSize: any,
  title: string,
  hospitalName?: string,
  adaptiveWidth: boolean = false,
  contentPadding?: string,
  output?: PrintOutputOptions
) => {
  // const isDesktop = window.innerWidth >= 1024 && navigator.maxTouchPoints === 0

  // if (!isDesktop) {
  //   ElMessage.warning('您当前设备不支持打印，请前往电脑端使用')
  //   return
  // }

  const displayTitle = hospitalName ? `${hospitalName} - ${title}` : title

  // 彻底隐藏原生页眉页脚：必须将 @page margin 设为 0
  // 高度 auto 只表示卷纸按内容增长，不能让整张纸退化成浏览器默认纸张。
  // 否则 58/80mm 小票会失去 @page 宽度，打印机常按 A4 缩放，导致字体变小。
  const isAutoPaper = !paperSize?.width || paperSize.width === 'auto'
  const isSmallPaper = /^\d+(?:\.\d+)?mm$/i.test(paperSize.width || '')
  const isInstructionPrint = title === '用药说明'
  const bottomSpaceHeight = isAutoPaper
    ? '0'
    : isInstructionPrint
    ? '30mm'
    : isSmallPaper
    ? '20mm'
    : '2mm'
  const bottomSpaceBorder = isAutoPaper
    ? 'none'
    : isInstructionPrint
    ? '1px dashed #000'
    : isSmallPaper
    ? '1px dashed #000'
    : 'none'
  const pageSizeCss = isAutoPaper
    ? 'auto'
    : `${paperSize.width} ${paperSize.height || 'auto'}`

  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title> </title>
      <style>
        /* 1. 针对卷纸打印优化：设置宽度并让高度自动增长 */
        @page { 
          size: ${pageSizeCss};
          margin: 0mm !important; 
        }
        
        html, body { 
          margin: 0 !important;
          padding: 0 !important;
          height: 100%;
          -webkit-print-color-adjust: exact;
        }
        
        body {
          /* 网页打印时小票需要固定宽度；客户端打印时由 paperSize 控制，body 保持 100% */
          width: ${
            adaptiveWidth && isSmallPaper
              ? paperSize.width
              : '100%'
          };
          font-family: 'SimSun', 'Microsoft YaHei', monospace;
        }

        .print-content { 
          width: 100%; 
          box-sizing: border-box; 
          /* 小票需要少量侧边距防贴边，A4 需要大边距 */
          padding: ${
            contentPadding ||
            (isSmallPaper ? '3mm 3mm' : '10mm 10mm 5mm 10mm')
          };
          word-wrap: break-word; 
          word-break: break-all; 
          min-height: 100%;
        }

        .print-wrapper {
          display: flex;
          flex-direction: column;
          min-height: ${
            paperSize.height && paperSize.height !== 'auto' ? '96vh' : 'auto'
          };
        }

        .print-main {
          flex: 1;
        }

        .print-footer {
          margin-top: auto;
          width: 100%;
        }

        .bottom-space {
          margin-top: ${bottomSpaceHeight} !important;
          border-bottom: ${bottomSpaceBorder};
          height: ${bottomSpaceHeight} !important;
          width: 100% !important;
          box-sizing: border-box;
        }

        @media print {
          body {
            margin: 0 !important;
            width: ${isSmallPaper ? paperSize.width : '100%'} !important;
          }
          .bottom-space {
            margin-top: ${bottomSpaceHeight} !important;
            border-bottom: ${bottomSpaceBorder};
            height: ${bottomSpaceHeight} !important;
            width: 100% !important;
          }
        }
      </style>
    </head>
    <body>
      <div class="print-content">${html}</div>
      <script>
        // 4. 在打印前最后一刻锁定标题为一个空格，防止显示网址
        document.title = " ";
        window.onload = function() {
          setTimeout(() => {
            window.print();
            setTimeout(() => {
              const iframe = window.frameElement;
              if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
            }, 500);
          }, 50);
        };
      <\/script>
    </body>
    </html>
  `
  const bridge = output ? getPrinterBridge() : undefined
  if (output && bridge) {
    if (output.isActive && !output.isActive()) throw new Error('登录机构已变化，打印已停止')
    const result = await bridge.print({
      deviceName: output.deviceName,
      copies: output.copies,
      preview: output.preview,
      title: displayTitle,
      paperSize: paperSize?.width
        ? { width: paperSize.width, height: paperSize.height }
        : undefined,
      html: htmlContent.replace(/<script>[\s\S]*?<\/script>/, ''),
    })
    if (result.status === 'failed') throw new Error(result.message || '打印失败')
    return result
  }

  const iframe = document.createElement('iframe')
  iframe.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:none'
  document.body.appendChild(iframe)

  const printWindow = iframe.contentWindow!
  printWindow.document.write(htmlContent)
  printWindow.document.close()
  return { status: 'browser' as const }
}

// 创建打印模板内容
const createPrintTemplate = (
  config: any,
  patientData: any,
  projectData: any
) => {
  const cashierInfo = normalizeReceiptSelection(config.cashierInfo)
  const levelInfo = normalizeReceiptSelection(config.levelInfo)
  const cardInfo = normalizeReceiptSelection(config.cardInfo)
  const chargeSummary =
    patientData.summary ||
    buildChargePrintSummary(
      patientData,
      projectData?.chargeRecords || [],
      projectData?.refundRecords || []
    )
  const memberDetail = projectData?.memberDetail
  const cardDetail = projectData?.cardDetail
  const cardRights = Array.isArray(projectData?.cardRights)
    ? projectData.cardRights
    : []
  let html = `
    <div style="width: 100%; font-size: ${
      config.fontSize || 12
    }px; line-height: ${
    (config.lineSpacing || 5) + 15
  }px; padding: 0; padding-bottom: 0; font-family: Arial, sans-serif; box-sizing: border-box;">
  `

  // 小票抬头
  if (config.settingName) {
    html += `<div style="text-align: center; font-weight: bold; font-size: ${
      (config.fontSize || 12) + 2
    }px; margin-bottom: ${config.lineSpacing || 5}px;">${
      config.settingName
    }</div>`
  }

  html += `<div style="text-align: center; margin-bottom: ${
    config.lineSpacing || 5
  }px;">收费单</div>`

  // 患者信息
  if (config.patientInfo && config.patientInfo.length > 0) {
    html += `<div style="margin-bottom: ${config.lineSpacing || 5}px;">`
    html += `姓名：${patientData.userName || ''}`
    if (config.patientInfo.includes('性别')) {
      html += ` ${
        patientData.sex == 2 || patientData.sex === '2'
          ? '女'
          : patientData.sex == 1 || patientData.sex === '1'
          ? '男'
          : ''
      }`
    }
    if (config.patientInfo.includes('年龄')) {
      if (patientData.ageYear) {
        html += ` ${patientData.ageYear}岁`
      }
      if (patientData.ageMonth) {
        html += ` ${patientData.ageMonth}月`
      }
      if (patientData.ageDay) {
        html += ` ${patientData.ageDay}天`
      }
    }
    html += `</div>`

    if (config.patientInfo.includes('手机') && patientData.mobile) {
      // 默认显示手机号，如果需要脱敏逻辑可以在这里添加
      let phone = patientData.mobile
      if (config.phoneDisplay === 2) {
        phone = phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
      }
      html += `<div style="margin-bottom: ${
        config.lineSpacing || 5
      }px;">手机：${phone}</div>`
    }

    if (config.patientInfo.includes('医生') && patientData.doctorName) {
      html += `<div style="margin-bottom: ${
        config.lineSpacing || 5
      }px;">医生：${patientData.doctorName}</div>`
    }

    if (config.patientInfo.includes('开单人') && patientData.prescriber) {
      html += `<div style="margin-bottom: ${
        config.lineSpacing || 5
      }px;">开单人：${patientData.prescriber}</div>`
    }
    if (config.patientInfo.includes('诊号')) {
      const visitNumber = getPatientVisitNumber(patientData)
      if (visitNumber) {
        html += `<div style="margin-bottom: ${
          config.lineSpacing || 5
        }px;">诊号：${visitNumber}</div>`
      }
    }
  }

  // 抬头信息
  if (config.headerInfo && config.headerInfo.includes('电话')) {
    html += `<div style="margin-bottom: ${
      config.lineSpacing || 5
    }px;">医院电话：${
      patientData.contactPhone || config.hospitalPhone || ''
    }</div>`
  }

  if (config.headerInfo && config.headerInfo.includes('地址')) {
    html += `<div style="margin-bottom: ${
      config.lineSpacing || 5
    }px;">医院地址：${
      patientData.clinicAddress || config.hospitalAddress || ''
    }</div>`
  }

  // 收费项目
  if (config.chargeItems && chargeSummary.items.length > 0) {
    html += `<div style="border-top: 1px dashed #000; margin: ${
      config.lineSpacing || 5
    }px 0; padding-top: ${config.lineSpacing || 5}px;">`
    html += `<div style="margin-bottom: ${
      config.lineSpacing || 5
    }px; font-weight: bold;">收费项目：</div>`

    chargeSummary.items.forEach((item: any) => {
      html += `<div style="display: flex; justify-content: space-between; margin-bottom: ${
        config.lineSpacing || 5
      }px;">
        <span>${item.name}×${item.quantity || 1}</span>
        <span style="min-width: 60px;">￥${formatMoney(item.amount)}元</span>
      </div>`
    })

    html += `<div style="display: flex; justify-content: space-between; border-top: 1px solid #000; margin-top: ${
      config.lineSpacing || 5
    }px; padding-top: ${config.lineSpacing || 5}px;">
      <span>合计：</span>
      <span>￥${formatMoney(chargeSummary.totalAmount)}元</span>
    </div>`
    html += `</div>`
  }

  // 收银信息
  if (cashierInfo.length > 0) {
    html += `<div style="border-top: 1px dashed #000; margin: ${
      config.lineSpacing || 5
    }px 0; padding-top: ${config.lineSpacing || 5}px;">`

    if (cashierInfo.includes('合计')) {
      html += `<div style="margin-bottom: ${
        config.lineSpacing || 5
      }px;">合计：￥${formatMoney(chargeSummary.totalAmount)}元</div>`
    }

    if (cashierInfo.includes('优惠')) {
      html += `<div style="margin-bottom: ${
        config.lineSpacing || 5
      }px;">优惠：-￥${formatMoney(chargeSummary.discountAmount)}元</div>`
    }

    if (cashierInfo.includes('应付')) {
      html += `<div style="margin-bottom: ${
        config.lineSpacing || 5
      }px;">应付：￥${formatMoney(chargeSummary.receivableAmount)}元</div>`
    }

    if (cashierInfo.includes('实付')) {
      html += `<div style="margin-bottom: ${
        config.lineSpacing || 5
      }px;">实付：￥${formatMoney(chargeSummary.actualPaidAmount)}元</div>`
    }

    html += `</div>`
  }

  const memberSectionLines: string[] = []
  if (levelInfo.includes('会员等级') && memberDetail?.levelName) {
    memberSectionLines.push(
      `<div style="margin-bottom: ${config.lineSpacing || 5}px;">会员等级：${
        memberDetail.levelName
      }</div>`
    )
  }
  if (levelInfo.includes('会员余额') && memberDetail) {
    memberSectionLines.push(
      `<div>会员余额：￥${formatMoney(getMemberBalance(memberDetail))}元</div>`
    )
  }
  if (memberSectionLines.length > 0) {
    html += `<div style="border-top: 1px dashed #000; margin: ${
      config.lineSpacing || 5
    }px 0; padding-top: ${config.lineSpacing || 5}px;">`
    html += memberSectionLines.join('')
    html += `</div>`
  }

  const cardSectionLines: string[] = []
  if (cardInfo.includes('卡项名称') && cardDetail?.cardName) {
    cardSectionLines.push(
      `<div style="margin-bottom: ${config.lineSpacing || 5}px;">卡项名称：${
        cardDetail.cardName
      }</div>`
    )
  }
  if (cardInfo.includes('剩余权益') && cardRights.length > 0) {
    cardSectionLines.push(
      `<div style="margin-bottom: ${
        config.lineSpacing || 5
      }px;">剩余权益：</div>`
    )
    cardRights.forEach((item: any, index: number) => {
      cardSectionLines.push(
        `<div style="margin-bottom: ${
          index === cardRights.length - 1 ? 0 : config.lineSpacing || 5
        }px; padding-left: 12px;">${item.projectName}：剩余${
          item.remainingCount
        }次</div>`
      )
    })
  }
  if (cardSectionLines.length > 0) {
    html += `<div style="border-top: 1px dashed #000; margin: ${
      config.lineSpacing || 5
    }px 0; padding-top: ${config.lineSpacing || 5}px;">`
    html += cardSectionLines.join('')
    html += `</div>`
  }

  // 页尾内容
  if (config.footerContent && config.footerContent.length > 0) {
    html += `<div style="border-top: 1px dashed #000; margin: ${
      config.lineSpacing || 5
    }px 0; padding-top: ${config.lineSpacing || 5}px;">`

    if (config.footerContent.includes('收费员')) {
      html += `<div style="margin-bottom: ${
        config.lineSpacing || 5
      }px;">收费员：${chargeSummary.chargeUserName || ''}</div>`
    }

    const now = new Date()
    const currentTime = now.toLocaleString('zh-CN')

    if (config.footerContent.includes('收费时间')) {
      html += `<div style="margin-bottom: ${
        config.lineSpacing || 5
      }px;">收费时间：${formatPrintDateTime(
        chargeSummary.chargeTime || currentTime
      )}</div>`
    }

    if (config.footerContent.includes('打印时间')) {
      html += `<div style="margin-bottom: ${
        config.lineSpacing || 5
      }px;">打印时间：${currentTime}</div>`
    }

    if (config.footerContent.includes('收费备注')) {
      const remarkText = chargeSummary.remark || '无'
      html += `<div style="margin-bottom: ${
        config.lineSpacing || 5
      }px;">收费备注：${remarkText}</div>`
    }

    html += `</div>`
  }

  // 公告提示
  if (config.announcement) {
    html += `<div style="margin: ${config.lineSpacing || 5}px 0; font-size: ${
      (config.fontSize || 12) - 2
    }px;">${config.announcement}</div>`
  }

  // 退换提醒
  if (config.footerContent && config.footerContent.includes('退换提醒')) {
    html += `<div style="text-align: center; margin-top: ${
      config.lineSpacing || 5
    }px;">药品离柜，概不退换</div>`
  }

  html += `</div>`

  // 添加底部空白区域，方便撕纸
  html += `<div class="bottom-space"></div>`

  return html
}

// 打印挂号小票
export const printRegistrationReceipt = async (
  registrationId: string | number,
  registrationType = 1
) => {
  try {
    const [res, detailRes] = await Promise.all([
      printRegistrationReceiptDetail(),
      getRegistrationReceiptPrint({ id: registrationId, registrationType }),
    ])
    if (detailRes.code !== 0 || !detailRes.data) {
      ElMessage.error('获取挂号打印信息失败')
      return
    }
    const registerData = detailRes.data
    if (res.code !== 0 || !res.data) {
      ElMessage.error('获取打印配置失败')
      return
    }
    const config = {
      ...res.data,
      headerInfo: normalizeReceiptSelection(res.data?.headerInfo),
      patientInfo: normalizeReceiptSelection(res.data?.patientInfo),
      sourceInfo: normalizeReceiptSelection(res.data?.sourceInfo),
      visitInfo: normalizeReceiptSelection(res.data?.visitInfo),
      feeInfo: normalizeReceiptSelection(res.data?.feeInfo),
      footerInfo: normalizeReceiptSelection(res.data?.footerInfo),
      phoneDisplay: [1, 2].includes(res.data?.phoneDisplay)
        ? res.data.phoneDisplay
        : 2,
    }
    const paperSize = getPaperSize(config, '80mm')
    const fontSize = Number(config.fontSize) || 14
    const lineSpacing = Number(config.lineSpacing) || 10
    const marginStyle = `margin-bottom: ${lineSpacing}px;`
    const checkHeader = (key: string) => config.headerInfo.includes(key)
    const checkPatient = (key: string) => config.patientInfo.includes(key)
    const checkSource = (key: string) => config.sourceInfo.includes(key)
    const checkVisit = (key: string) => config.visitInfo.includes(key)
    const checkFee = (key: string) => config.feeInfo.includes(key)
    const checkFooter = (key: string) => config.footerInfo.includes(key)
    const appendLine = (
      label: string,
      value: any,
      bold = false,
      fontSizeOffset = 0
    ) => {
      if (value === undefined || value === null || value === '') return ''
      return `<div style="${marginStyle}${
        bold ? ' font-weight: bold;' : ''
      }${
        fontSizeOffset ? ` font-size: ${fontSize + fontSizeOffset}px;` : ''
      }">${label}：${value}</div>`
    }
    const getSexText = (sex: any) =>
      sex === 1 || sex === '1' ? '男' : sex === 2 || sex === '2' ? '女' : ''
    const getPhoneText = (phone: any) => {
      if (!phone || config.phoneDisplay === 1) return phone
      return String(phone).replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2')
    }
    const getAgeText = () => {
      if (registerData.age) return registerData.age
      const parts = [
        registerData.ageYear ? `${registerData.ageYear}岁` : '',
        registerData.ageMonth ? `${registerData.ageMonth}月` : '',
        registerData.ageDay ? `${registerData.ageDay}天` : '',
      ].filter(Boolean)
      return parts.join('')
    }
    const getFixedTimeText = (value: any) =>
      value == 1 ? '上午' : value == 2 ? '下午' : value == 3 ? '晚上' : ''
    const getAppointmentTime = () => {
      if (registerData.startTime && registerData.endTime) {
        return `${registerData.startTime}~${registerData.endTime}`
      }
      return ''
    }
    const getVisitStatusText = () => {
      if (registerData.visitStatusLabel) return registerData.visitStatusLabel
      if (registerData.visitStatus == 1) return '初诊'
      if (registerData.visitStatus == 2) return '复诊'
      return ''
    }
    const appointmentItem = registerData.appointmentItem || ''

    let html = `
    <div style="font-size: ${fontSize}px; line-height: ${
      lineSpacing + 15
    }px; padding: 0; padding-bottom: 0; font-family: Arial, sans-serif; box-sizing: border-box;">
    `

    // 小票抬头
    if (config.settingName) {
      html += `<div style="text-align: center; font-weight: bold; font-size: ${
        fontSize + 2
      }px; margin-bottom: ${lineSpacing}px;">${config.settingName}</div>`
    }

    html += `<div style="text-align: center; margin-bottom: ${lineSpacing}px;">挂号小票</div>`

    // 患者信息
    const patientInfoHtml = (() => {
      let patientHtml = ''
      const sexText = checkPatient('性别') ? getSexText(registerData.sex) : ''
      const ageText = checkPatient('年龄') ? getAgeText() : ''
      patientHtml += `<div style="${marginStyle}">姓名：${
        registerData.userName || ''
      }${sexText ? ` ${sexText}` : ''}${ageText ? ` ${ageText}` : ''}</div>`
      if (checkPatient('生日')) {
        patientHtml += appendLine('生日', registerData.birthday)
      }
      if (checkPatient('手机')) {
        patientHtml += appendLine('手机', getPhoneText(registerData.mobile))
      }
      return patientHtml
    })()

    html += `<div style="border-bottom: 1px dashed #000; margin-bottom: ${lineSpacing}px; padding-bottom: ${lineSpacing}px;">`
    html += patientInfoHtml
    if (checkHeader('电话')) {
      html += appendLine(
        '医院电话',
        registerData.contactPhone || config.hospitalPhone
      )
    }
    if (checkHeader('地址')) {
      html += appendLine(
        '医院地址',
        registerData.clinicAddress || config.hospitalAddress
      )
    }
    html += `</div>`

    // 号源信息
    let sourceHtml = ''
    if (checkSource('医生')) {
      sourceHtml += appendLine('医生', registerData.doctorName)
    }
    if (checkSource('科室')) {
      sourceHtml += appendLine('科室', registerData.departmentName)
    }
    if (checkSource('序号')) {
      const visitNumber = getPatientVisitNumber(registerData)
      const fixedTimeText = getFixedTimeText(registerData.fixedTime)
      const numberText = visitNumber
        ? `${fixedTimeText || ''}${visitNumber}号`
        : ''
      sourceHtml += appendLine('序号', numberText, true, 2)
    }
    if (checkSource('初复诊')) {
      sourceHtml += appendLine('初复诊', getVisitStatusText())
    }
    if (sourceHtml) {
      html += `<div style="border-bottom: 1px dashed #000; margin-bottom: ${lineSpacing}px; padding-bottom: ${lineSpacing}px;">${sourceHtml}</div>`
    }

    // 就诊信息
    let visitHtml = ''
    if (checkVisit('就诊日期')) {
      visitHtml += appendLine('就诊日期', registerData.applyDate)
    }
    if (checkVisit('就诊时间')) {
      visitHtml += appendLine('就诊时间', getAppointmentTime())
    }
    if (checkVisit('签到时间')) {
      visitHtml += appendLine(
        '签到时间',
        registerData.signTime
          ? custFormatDate(registerData.signTime, 'YYYY-MM-DD HH:mm:ss')
          : ''
      )
    }
    if (checkVisit('就诊诊室')) {
      visitHtml += appendLine('就诊诊室', registerData.medicalRoom)
    }
    if (checkVisit('预约项目')) {
      visitHtml += appendLine('预约项目', appointmentItem)
    }
    if (checkVisit('就诊备注')) {
      visitHtml += appendLine('就诊备注', registerData.remark)
    }
    if (visitHtml) {
      html += `<div style="border-bottom: 1px dashed #000; margin-bottom: ${lineSpacing}px; padding-bottom: ${lineSpacing}px;">${visitHtml}</div>`
    }

    // 费用信息
    let feeHtml = ''
    if (checkFee('金额')) {
      feeHtml += appendLine('金额', `${formatMoney(registerData.totalPrice)}元`)
    }
    if (checkFee('实付')) {
      const payChannel = registerData.payChannelCode
        ? `（${getDictLabel(
            DICT_TYPE.CLINIC_PAY_CHANNEL,
            registerData.payChannelCode
          )}）`
        : ''
      feeHtml += appendLine(
        '实付',
        `${payChannel}${formatMoney(registerData.payPrice)}元`
      )
    }
    if (feeHtml) {
      html += `<div style="border-bottom: 1px dashed #000; margin-bottom: ${lineSpacing}px; padding-bottom: ${lineSpacing}px;">${feeHtml}</div>`
    }

    // 页尾
    let footerHtml = ''
    if (checkFooter('操作员')) {
      footerHtml += appendLine('操作员', registerData.openUserName)
    }
    if (checkFooter('预约日期')) {
      footerHtml += appendLine(
        '预约日期',
        registerData.createTime
          ? custFormatDate(registerData.createTime, 'YYYY-MM-DD')
          : ''
      )
    }
    if (checkFooter('打印时间')) {
      footerHtml += appendLine(
        '打印时间',
        custFormatDate(new Date(), 'YYYY-MM-DD HH:mm:ss')
      )
    }
    if (checkFooter('地址')) {
      footerHtml += appendLine(
        '地址',
        registerData.clinicAddress || config.hospitalAddress
      )
    }
    if (checkFooter('电话')) {
      footerHtml += appendLine(
        '电话',
        registerData.contactPhone || config.hospitalPhone
      )
    }
    if (footerHtml) {
      html += `<div style="border-bottom: 1px dashed #000; margin-bottom: ${lineSpacing}px; padding-bottom: ${lineSpacing}px;">${footerHtml}</div>`
    }

    if (config.announcement) {
      html += `<div style="${marginStyle}">${config.announcement}</div>`
    }
    if (checkFooter('过期提醒')) {
      html += `<div style="text-align: center; margin-top: ${lineSpacing}px;">当日有效 过期作废</div>`
    }

    // 底部空白
    html += `<div class="bottom-space"></div>`
    html += `</div>`

    executePrint(html, paperSize, '挂号小票', getHospitalName(config))
  } catch (error) {
    console.error('打印失败:', error)
    ElMessage.error('打印失败')
  }
}

// 打印收费小票
export const printChargeReceipt = async (
  medicalId: string | number,
  printConfigParams?: PrintConfigParams,
  output?: PrintOutputOptions
) => {
  try {
    const res = await printRestoreDetail()
    if (res.code !== 0 || !res.data) {
      if (output) throw new Error('获取打印配置失败')
      ElMessage.error('获取打印配置失败')
      return
    }
    const config = res.data
    const levelInfo = normalizeReceiptSelection(config.levelInfo)
    const cardInfo = normalizeReceiptSelection(config.cardInfo)
    const paperSize = getPaperSize(config, '80mm')

    const detailRes = await getChargeReceiptPrint({
      id: medicalId,
    })
    if (detailRes.code !== 0) {
      if (output) throw new Error('获取病历详情失败')
      ElMessage.error('获取病历详情失败')
      return
    }
    const patientData = detailRes.data

    let memberDetail: any = null
    let cardDetail: any = null
    let cardRights: any[] = []

    const extraDataTasks: Promise<void>[] = []

    if (levelInfo.length > 0 && patientData?.memberLevelId) {
      extraDataTasks.push(
        getPatientLevelById(patientData.memberLevelId)
          .then(memberRes => {
            if (memberRes.code === 0 && memberRes.data) {
              memberDetail = memberRes.data
            }
          })
          .catch(error => {
            console.error('获取会员打印信息失败:', error)
          })
      )
    }

    if (cardInfo.includes('卡项名称') && patientData?.memberCardId) {
      extraDataTasks.push(
        getPatientCardById(patientData.memberCardId)
          .then(cardRes => {
            if (cardRes.code === 0 && cardRes.data) {
              cardDetail = cardRes.data
            }
          })
          .catch(error => {
            console.error('获取卡项打印信息失败:', error)
          })
      )
    }

    if (cardInfo.includes('剩余权益') && patientData?.memberCardId) {
      extraDataTasks.push(
        patientCardDetail({ id: patientData.memberCardId })
          .then(cardRightsRes => {
            if (cardRightsRes.code === 0 && cardRightsRes.data) {
              cardRights = buildCardRightsList(cardRightsRes.data)
            }
          })
          .catch(error => {
            console.error('获取卡项剩余权益失败:', error)
          })
      )
    }

    if (extraDataTasks.length > 0) {
      await Promise.all(extraDataTasks)
    }

    const printHTML = createPrintTemplate(config, patientData, {
      memberDetail,
      cardDetail,
      cardRights,
    })

    return await executePrint(
      printHTML,
      paperSize,
      '收费小票',
      getHospitalName(config),
      true,
      undefined,
      output
    )
  } catch (error) {
    if (output) throw error
    console.error('打印失败:', error)
    ElMessage.error('打印失败')
  }
}

// 打印退费小票
export const printRefundReceipt = async (
  medicalId: string | number,
  printConfigParams?: PrintConfigParams,
  output?: PrintOutputOptions,
  options: { includeDrugReturnDetails?: boolean } = {}
) => {
  try {
    const isDrugReturn = options.includeDrugReturnDetails === true
    const [res, configRes, payments] = await Promise.all([
      isDrugReturn ? getWithdrawalDrugReceiptPrint({ id: medicalId }) : getRefundReceiptPrint({ id: medicalId }), printRestoreDetail(),
      isDrugReturn ? Promise.resolve([]) : getRefundPaymentRecords(medicalId),
    ])
    if (res.code !== 0 || !res.data) throw new Error(res.msg || (isDrugReturn ? '获取退药打印数据失败' : '获取退费打印数据失败'))
    if (configRes.code !== 0 || !configRes.data) throw new Error(configRes.msg || '获取退费打印配置失败')
    const config = configRes.data
    const patientData = res.data
    if (isDrugReturn) {
      return await executePrint(renderDrugReturnReceipt(patientData, config),
        getPaperSize({ paperSize: '80mm' }), '退药小票', getHospitalName(config), true, undefined, output)
    }
    return await executePrint(renderChargeRefundReceipt(patientData, config, payments),
      getPaperSize({ paperSize: '80mm' }), '退费小票', getHospitalName(config), true, '1mm 1mm', output)
  } catch (error) {
    if (output) throw error
    console.error('打印失败:', error)
    ElMessage.error(getPrintFailureMessage(error))
  }
}

// 打印发药小票
export const printDispensingReceipt = async (
  medicalId: string | number,
  options: { allowLinkedInstructions?: boolean } = {},
  output?: PrintOutputOptions
) => {
  try {
    const [res, settingRes] = await Promise.all([
      printDispensingDetail(),
      options.allowLinkedInstructions
        ? getDispensingSettingDetail().catch(() => null)
        : Promise.resolve(null),
    ])
    if (res.code !== 0 || !res.data) {
      if (output) throw new Error('获取打印配置失败')
      ElMessage.error('获取打印配置失败')
      return
    }
    const config = res.data
    const autoPrintDrugInstructions =
      settingRes?.code === 0
        ? settingRes.data?.autoPrintDrugInstructions ?? false
        : false
    const isA5 = config.printFormat === 2
    const paperSize = getPaperSize({ paperSize: isA5 ? 'A5' : '80mm' })

    const detailRes = await getDispensingReceiptPrint({
      id: medicalId,
    })
    if (detailRes.code !== 0) {
      if (output) throw new Error('获取病历详情失败')
      ElMessage.error('获取病历详情失败')
      return
    }
    const patientData = detailRes.data

    const checkPat = (key: string) => config.patientInfo?.includes(key)
    const checkXi = (key: string) => config.westernMedicine?.includes(key)
    const checkChin = (key: string) => config.chineseMedicine?.includes(key)
    const checkGoods = (key: string) => config.materialGoods?.includes(key)
    const checkAdvice = (key: string) => config.medicalAdvice?.includes(key)
    const checkFoot = (key: string) => config.footerContent?.includes(key)
    const showChineseSingleDose = checkChin('单剂克数')
    const showChineseTotalDose = checkChin('总剂量')
    const chineseMedicineLayout =
      Number(config.chineseMedicineLayout) === 2 ? 2 : 3
    const chineseColumnWidth = chineseMedicineLayout === 2 ? '50%' : '33.33%'

    const fontSize = config.fontSize || 12
    const lineSpacing = config.lineSpacing || 5
    const lineHeight = fontSize + lineSpacing

    // Base styles
    const baseStyle = `font-size: ${fontSize}px; line-height: ${lineHeight}px; font-family: Arial, sans-serif; box-sizing: border-box; width: 100%;`
    const marginStyle = `margin-bottom: ${lineSpacing}px;`
    const borderTopStyle = `border-top: 1px dashed #000; padding-top: ${lineSpacing}px; margin-top: ${lineSpacing}px;`
    let isFirstSection = true
    const getSectionStyle = () => {
      if (isFirstSection) {
        isFirstSection = false
        return `margin-top: ${lineSpacing}px;`
      }
      return borderTopStyle
    }
    const flexStyle = `display: flex; flex-wrap: wrap; gap: ${lineSpacing}px;`
    const flexBetween = `display: flex; justify-content: space-between;`
    const chineseListStyle = `display: flex; flex-wrap: wrap; row-gap: ${lineSpacing}px;`
    const chineseItemStyle = `width: ${chineseColumnWidth}; max-width: ${chineseColumnWidth}; box-sizing: border-box; padding-right: 12px;`

    let html = `<div style="${baseStyle}">`

    // Header
    if (config.settingName) {
      html += `<div style="text-align: center; font-weight: bold; font-size: ${
        fontSize + 4
      }px; ${marginStyle}">${config.settingName}</div>`
    }
    html += `<div style="text-align: center; font-weight: bold; font-size: ${
      fontSize + 2
    }px; ${marginStyle}">调剂发药单</div>`

    // Patient Info
    html += `<div style="${marginStyle} ${flexStyle} border-bottom: 2px solid #000; padding-bottom: ${lineSpacing}px;">`
    html += `<span style="margin-right: 15px;">姓名：${
      patientData.userName || ''
    }</span>`
    if (checkPat('性别'))
      html += `<span style="margin-right: 15px;">${
        patientData.sex === 1 ? '男' : '女'
      }</span>`
    if (checkPat('年龄'))
      html += `<span style="margin-right: 15px;">${
        patientData.ageYear || 0
      }岁</span>`

    if (checkPat('手机')) {
      let phone = patientData.mobile || ''
      if (config.phoneDisplay === 2)
        phone = phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
      html += `<span style="margin-right: 15px;">手机：${phone}</span>`
    }
    if (checkPat('诊号'))
      html += `<span style="margin-right: 15px;">诊号：${
        getPatientVisitNumber(patientData)
      }</span>`
    if (checkPat('医生'))
      html += `<span style="margin-right: 15px;">医生：${
        patientData.doctorName || ''
      }</span>`
    if (checkPat('科室'))
      html += `<span style="margin-right: 15px;">科室：${
        patientData.departmentsName || patientData.departmentName || ''
      }</span>`

    if (checkPat('诊断') && patientData.diagnosis) {
      html += `<div style="width: 100%;">诊断：${patientData.diagnosis}</div>`
    }
    html += `</div>`

    // Medicines
    const westernList: any[] = []
    const chineseList: any[] = []
    const materialList: any[] = []

    if (patientData.prescriptionList) {
      patientData.prescriptionList.forEach((p: any) => {
        if (p.prescriptionType === 1) chineseList.push(p)
        else if (p.prescriptionType === 2) westernList.push(p)
        else if (p.prescriptionType !== 4) materialList.push(p) // Exclude type 4 (Treatment Items)
      })
    }

    if (output && !westernList.length && !chineseList.length && !materialList.length) return

    // Western Medicine
    if (westernList.length > 0) {
      html += `<div style="${getSectionStyle()}">`
      westernList.forEach((p: any) => {
        if (p.drugList) {
          p.drugList.forEach((d: any) => {
            html += `<div style="${marginStyle}">`
            html += `<div style="${flexBetween}">`
            html += `<span>${d.drugName} ${
              checkXi('规格') && d.spec
                ? `<span style="color: #666;">(${d.spec})</span>`
                : ''
            }</span>`
            html += `<span>${d.quantity || 0}${d.unit || ''}</span>`
            html += `</div>`
            if (checkXi('厂家') && d.manufacturer) {
              html += `<div style="color: #666; font-size: 0.9em;">厂家：${d.manufacturer}</div>`
            }
            html += `</div>`
          })
        }
      })
      html += `</div>`
    }

    // Material Goods (Similar to Western)
    if (materialList.length > 0) {
      html += `<div style="${getSectionStyle()}">`
      materialList.forEach((p: any) => {
        if (p.drugList) {
          p.drugList.forEach((d: any) => {
            html += `<div style="${marginStyle}">`
            html += `<div style="${flexBetween}">`
            html += `<span>${d.drugName} ${
              checkGoods('规格') && d.spec
                ? `<span style="color: #666;">(${d.spec})</span>`
                : ''
            }</span>`
            html += `<span>${d.quantity || 0}${d.unit || ''}</span>`
            html += `</div>`
            if (checkGoods('厂家') && d.manufacturer) {
              html += `<div style="color: #666; font-size: 0.9em;">厂家：${d.manufacturer}</div>`
            }
            html += `</div>`
          })
        }
      })
      html += `</div>`
    }

    // Chinese Medicine
    if (chineseList.length > 0) {
      html += `<div style="${getSectionStyle()}">`
      chineseList.forEach((p: any) => {
        html += `<div style="${marginStyle} ${chineseListStyle}">`
        if (p.drugList) {
          p.drugList.forEach((d: any) => {
            html += `<div style="${chineseItemStyle}">`
            html += `<span>${d.drugName}</span>`
            if (checkChin('柜号') && d.cabinetNo)
              html += `<span>(${d.cabinetNo})</span>`
            if (checkChin('单个药品调剂方法') && d.usage)
              html += `<span>(${d.usage})</span>`
            if (checkChin('批号') && d.batchNo)
              html += `<span>[${d.batchNo}]</span>`
            const weights: string[] = []
            if (showChineseSingleDose) {
              const singleWeightVal = d.dosage
                ? parseFloat(d.dosage)
                : p.quantity
                ? parseFloat(d.quantity) / p.quantity
                : 0
              const singleWeight = singleWeightVal
                ? parseFloat(singleWeightVal.toFixed(2)).toString() +
                  (d.unit || 'g')
                : ''
              if (singleWeight) weights.push(singleWeight)
            }
            if (showChineseTotalDose) {
              weights.push(`${d.quantity || 0}${d.unit || ''}`)
            }
            if (weights.length > 0) {
              html += ` <span>${weights.join('/')}</span>`
            }
            html += `</div>`
          })
        }
        html += `</div>`

        const totalWeight =
          p.drugList?.reduce(
            (sum: number, d: any) => sum + (parseFloat(d.quantity) || 0),
            0
          ) || 0
        const chineseSummary: string[] = []
        if (showChineseSingleDose) {
          chineseSummary.push(
            `单剂${parseFloat((totalWeight / (p.quantity || 1)).toFixed(2))}g`
          )
        }
        if (showChineseTotalDose) {
          chineseSummary.push(`总重${parseFloat(totalWeight.toFixed(2))}g`)
        }
        html += `<div style="${marginStyle}">`
        html += `共${p.quantity || 0}剂`
        if (chineseSummary.length > 0) {
          html += `(${p.drugList?.length || 0}味 ${chineseSummary.join(',')})`
        }
        html += `</div>`

        if (p.usage) {
          html += `<div style="${marginStyle}">【用法】${p.usage}</div>`
        }

        if (checkAdvice('加工信息') && p.processing) {
          const totalBags =
            (Number(p.processing.quantity) || 0) *
            (Number(p.processing.drugDosage) || 0)
          const processingText = `${p.processing.processingName || ''}${
            p.processing.drugDosage ? `，${p.processing.drugDosage}剂` : ''
          }${totalBags ? `煎${totalBags}袋` : ''}${
            p.processing.processingUserName
              ? `，加工人：${p.processing.processingUserName}`
              : ''
          }`
          html += `<div style="${marginStyle}">【加工】${processingText}</div>`
        }
      })
      html += `</div>`
    }

    // Advice
    if (checkAdvice('快递信息') && patientData.expressInfo) {
      html += `<div style="${getSectionStyle()}">快递：${
        patientData.expressInfo
      }</div>`
    }

    const hasMedicines =
      westernList.length > 0 ||
      materialList.length > 0 ||
      chineseList.length > 0

    if (checkAdvice('医嘱') && patientData.medicalAdvice) {
      html += `<div style="${getSectionStyle()} border-bottom: 2px solid #000; padding-bottom: ${lineSpacing}px;">`
      html += `<div>医嘱：</div>`
      html += `<div style="white-space: pre-wrap;">${patientData.medicalAdvice}</div>`
      html += `</div>`
    } else {
      // If no medical advice, add border bottom to separate from footer
      if (hasMedicines) {
        html += `<div style="border-bottom: 2px solid #000; margin-top: ${lineSpacing}px;"></div>`
      }
    }

    // Footer
    html += `<div style="padding-top: ${lineSpacing}px;">`
    let footerAmount = 0
    let footerExtraFeesHtml = ''
    const processTotal = getMergedProcessingList(patientData).reduce(
      (sum: number, p: any) => sum + (p.totalPrice || p.payPrice || 0),
      0
    )
    if (config.invoiceAmountDisplay == 2) {
      const drugTotal =
        patientData.prescriptionList
          ?.filter((p: any) => p.prescriptionType !== 4)
          .reduce((sum: number, p: any) => sum + getPrescriptionAmount(p), 0) ||
        0
      footerAmount = drugTotal + processTotal
    } else if (config.invoiceAmountDisplay == 3) {
      const drugTotal =
        patientData.prescriptionList
          ?.filter((p: any) => p.prescriptionType !== 4)
          .reduce((sum: number, p: any) => sum + getPrescriptionAmount(p), 0) ||
        0
      const regFee =
        patientData.registerPrice ||
        patientData.registrationFee ||
        patientData.registrationPrice ||
        0
      const deliveryTotal = (patientData.deliveriesList || []).reduce(
        (sum: number, d: any) => sum + (d.totalPrice || d.payPrice || 0),
        0
      )
      const extraTotal = (patientData.extraList || []).reduce(
        (sum: number, e: any) => sum + (e.totalPrice || e.payPrice || 0),
        0
      )
      footerAmount =
        drugTotal + regFee + processTotal + deliveryTotal + extraTotal

      if (regFee > 0) {
        footerExtraFeesHtml += `<div style="display: flex; justify-content: space-between; margin-bottom: ${lineSpacing}px;"><span>挂号费</span><span>￥${formatMoney(
          regFee
        )}元</span></div>`
      }
      ;(patientData.extraList || []).forEach((e: any) => {
        footerExtraFeesHtml += `<div style="display: flex; justify-content: space-between; margin-bottom: ${lineSpacing}px;"><span>${
          e.extraName || e.name || '附加费'
        }</span><span>￥${formatMoney(
          e.totalPrice || e.payPrice
        )}元</span></div>`
      })
    } else {
      footerAmount = patientData.totalPrice || 0
    }

    if (footerExtraFeesHtml) {
      html += `<div style="margin-bottom: ${lineSpacing}px;">${footerExtraFeesHtml}</div>`
    }

    html += `<div style="${marginStyle} ${flexStyle} justify-content: space-between;">`
    if (config.invoiceAmountDisplay != 1) {
      html += `<span>金额：${formatMoney(footerAmount)}元</span>`
    }
    html += `<span>医生：${patientData.doctorName || ''}</span>`
    if (checkFoot('收费员'))
      html += `<span>收费员：${patientData.chargeUserName || ''}</span>`
    if (checkFoot('调配'))
      html += `<span>调配：${patientData.dispenserName || ''}</span>`
    if (checkFoot('地址'))
      html += `<span>地址：${
        patientData.clinicAddress || config.hospitalAddress || ''
      }</span>`
    if (checkFoot('电话'))
      html += `<span>电话：${
        patientData.contactPhone || config.hospitalPhone || ''
      }</span>`
    if (checkFoot('打印时间'))
      html += `<span>打印时间：${custFormatDate(
        new Date(),
        'YYYY-MM-DD HH:mm:ss'
      )}</span>`
    html += `</div>`

    if (config.announcement) {
      html += `<div style="${marginStyle} color: #666; font-size: 0.9em;">${config.announcement}</div>`
    }

    if (checkFoot('退换提醒')) {
      html += `<div style="text-align: center; margin-top: 20px;">药品离柜，概不退换</div>`
    }

    html += `</div>` // End Footer
    html += `</div>` // End Base

    html += `<div class="bottom-space"></div>`

    const result = await executePrint(html, paperSize, '发药小票', getHospitalName(config), true, undefined, output)

    if (autoPrintDrugInstructions && result.status !== 'cancelled') {
      await wait(LINKED_PRINT_DELAY)
      await printMedicationInstruction(medicalId, true)
    }
    return result
  } catch (error) {
    if (output) throw error
    console.error('打印失败:', error)
    ElMessage.error('打印失败')
  }
}

// 打印病历
export const printMedicalRecord = async (
  medicalId: string | number
) => {
  try {
    const res = await printMedicalDetail()
    if (res.code !== 0 || !res.data) {
      ElMessage.error('获取打印配置失败')
      return
    }
    const config = res.data
    const paperSize = getPaperSize(config, 'A4')
    const isSmallPaper =
      paperSize.width === '58mm' || paperSize.width === '80mm'

    const detailRes = await getMedicalRecordPrint({
      id: medicalId,
    })
    if (detailRes.code !== 0) {
      ElMessage.error('获取病历详情失败')
      return
    }
    const detailData = detailRes.data
    await fillChineseMedicineCabinetNumbers(detailData.prescriptionList || [])
    const patientInfo = {
      userName: detailData.userName || '',
      sex: detailData.sex === 1 ? '男' : detailData.sex === 2 ? '女' : '',
      ageYear: detailData.ageYear || '',
      visitNumber: getPatientVisitNumber(detailData),
      visitDate: detailData.visitDate || detailData.createTime,
      visitStatus:
        detailData.visitStatus === 1
          ? '初诊'
          : detailData.visitStatus === 2
          ? '复诊'
          : '',
      cardNo: detailData.cardNo || '',
      birthday: detailData.birthDate
        ? Array.isArray(detailData.birthDate)
          ? detailData.birthDate.join('-')
          : detailData.birthDate
        : '',
      weight: detailData.weight ? detailData.weight + 'kg' : '',
      marriage: detailData.marriage || '',
      nation: detailData.nation || '',
      work: detailData.work || '',
      workUnit: detailData.workUnit || '',
      address: detailData.address || '',
      departmentsName: detailData.departmentsName || '',
      mobile: detailData.mobile || '',
      chiefComplaint: detailData.chiefComplaint || '',
      diagnosis: detailData.diagnosis || '',
      presentIllness: detailData.presentIllness || '',
      pastIllness: detailData.pastIllness || '',
      familyIllness: detailData.familyIllness || '',
      allergy: detailData.allergy || '',
      personalHistory: detailData.personalHistory || '',
      menstrualMaritalHistory: detailData.menstrualMaritalHistory || '',
      inspectionAuscultation: detailData.inspectionAuscultation || '',
      syndromeDifferentiation: detailData.syndromeDifferentiation || '',
      treatmentMethod: detailData.treatmentMethod || '',
      medicalAdvice: detailData.medicalAdvice || '',
      doctorName: detailData.doctorName || '',
      totalPrice:
        detailData.payPrice && Number(detailData.payPrice) > 0
          ? detailData.payPrice
          : detailData.totalPrice && Number(detailData.totalPrice) > 0
          ? detailData.totalPrice
          : (detailData.prescriptionList || [])
              .filter((p: any) => p.prescriptionType !== 4)
              .reduce(
                (sum: number, p: any) =>
                  sum + (Number(p.payPrice) || Number(p.totalPrice) || 0),
                0
              )
              .toFixed(2),
    }

    const checkPat = (key: string) => config.patientInfo?.includes(key)
    const checkMed = (key: string) => config.medicineDisplay?.includes(key)
    const checkFoot = (key: string) => config.footerContent?.includes(key)
    const hasContent = (val: any) =>
      val === 0 ||
      (val !== undefined && val !== null && String(val).trim() !== '')
    const isEnabledFlag = (val: any) => val === true || val === 1 || val === '1'
    const showTreatmentContent = isEnabledFlag(config.treatmentContent)
    const packageDisplay = Number(config.packageDisplay)
    const getMedicalTreatmentPrintItems = (items: any[]) =>
      items.flatMap((item: any) => {
        if (Number(item.category) !== 3) return [item]

        const packageChildren = Array.isArray(item.comboItemList)
          ? item.comboItemList.map((child: any) => ({
              ...child,
              __packageChild: true,
            }))
          : []
        if (packageDisplay === 1) return [item]
        if (packageDisplay === 2) return packageChildren
        if (packageDisplay === 3) return [item, ...packageChildren]
        return []
      })

    // 格式化日期
    const formatVisitDate = (date: any) => {
      if (!date) return ''
      const d = new Date(date)
      if (config.visitDateDisplay === 1) {
        return custFormatDate(d, 'YYYY-MM-DD')
      } else {
        return custFormatDate(d, 'YYYY-MM-DD HH:mm:ss')
      }
    }

    // 格式化手机号
    const formatPhone = (phone: string) => {
      if (!phone) return ''
      if (config.phoneDisplay === 1) return phone
      if (config.phoneDisplay === 2)
        return phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
      return ''
    }

    let html = `${
      isSmallPaper
        ? ''
        : `<style>
          @media print {
            .medical-record-layout {
              width: 100%;
              border-collapse: collapse;
            }
            .medical-record-layout > tfoot {
              display: table-footer-group;
            }
            .medical-record-layout > thead {
              display: table-header-group;
            }
            .medical-record-header {
              padding-top: 7mm;
              break-inside: avoid;
              page-break-inside: avoid;
            }
            .medical-record-footer-space {
              height: 32mm;
            }
            .medical-record-footer {
              position: fixed;
              left: 10mm;
              right: 10mm;
              bottom: 5mm;
              box-sizing: border-box;
              background: #fff;
              break-inside: avoid;
              page-break-inside: avoid;
            }
          }
        </style>`
    }<table class="medical-record-layout" style="width: 100%; table-layout: fixed; overflow-wrap: anywhere; border-collapse: collapse; font-size: ${
      config.fontSize || 14
    }px; line-height: ${
      (config.lineSpacing || 10) + (config.fontSize || 14)
    }px; font-family: Arial, sans-serif;">
      <tfoot><tr><td><div class="medical-record-footer-space"></div></td></tr></tfoot>
      <thead><tr><td><div class="medical-record-header">`

    // 抬头名称
    if (config.settingName && config.settingName.length > 0) {
      config.settingName.forEach((name: string) => {
        if (name) {
          html += `<div style="text-align: center; font-weight: bold; font-size: ${
            (config.fontSize || 14) + 2
          }px; margin-bottom: ${config.lineSpacing || 10}px;">${name}</div>`
        }
      })
    }

    // 门诊病历标题
    if (config.headerInfo) {
      html += `<div style="text-align: center; font-weight: bold; font-size: ${
        (config.fontSize || 14) + 2
      }px; margin-bottom: ${config.lineSpacing || 10}px;">门诊病历</div>`
    }

    // 患者信息区域
    html += `<div style="width: 100%; display: flex; flex-wrap: wrap; padding-bottom: ${
      config.lineSpacing || 10
    }px; border-bottom: 1px solid #000; margin-bottom: ${
      config.lineSpacing || 10
    }px;">`

    const appendPatientField = (
      label: string,
      value: any,
      fullWidth = false
    ) => {
      if (!hasContent(value)) return
      html += `<span style="margin-right: 20px;${
        fullWidth ? ' width: 100%;' : ''
      }">${label}：${value}</span>`
    }

    // 固定显示姓名、性别、年龄
    const ageText = [
      patientInfo.ageYear ? `${patientInfo.ageYear}岁` : '',
      patientInfo.ageMonth ? `${patientInfo.ageMonth}月` : ''
    ].filter(Boolean).join('')
    const nameAgeText = [
      patientInfo.userName,
      patientInfo.sex,
      ageText
    ].filter(Boolean).join(' ')
    appendPatientField('姓名', nameAgeText)
    appendPatientField('日期', formatVisitDate(patientInfo.visitDate))

    if (checkPat('初复诊'))
      appendPatientField('初复诊', patientInfo.visitStatus)
    if (checkPat('诊号')) appendPatientField('诊号', patientInfo.visitNumber)
    if (checkPat('身份证')) appendPatientField('身份证', patientInfo.cardNo)
    if (checkPat('出生日期'))
      appendPatientField('出生日期', patientInfo.birthday)
    if (checkPat('体重')) appendPatientField('体重', patientInfo.weight)
    if (checkPat('婚否')) appendPatientField('婚否', patientInfo.marriage)
    if (checkPat('民族')) appendPatientField('民族', patientInfo.nation)
    if (checkPat('职业')) appendPatientField('职业', patientInfo.work)
    if (checkPat('工作单位'))
      appendPatientField('工作单位', patientInfo.workUnit)
    if (checkPat('住址')) appendPatientField('住址', patientInfo.address, true)
    if (checkPat('科室'))
      appendPatientField('科室', patientInfo.departmentsName)
    if (config.phoneDisplay !== 3)
      appendPatientField('手机', formatPhone(patientInfo.mobile))

    html += `</div></div></td></tr></thead>
      <tbody><tr><td><div class="medical-record-body">`

    // 病历正文：使用紧凑行距，避免短字段占用过多纵向空间
    const medicalFieldLineHeight = (config.fontSize || 14) + 4
    const medicalFieldSpacing = Math.min(config.lineSpacing || 10, 4)
    html += `<div style="margin-bottom: ${medicalFieldSpacing}px;">`
    const medicalFieldRows = [
      { label: '主诉', value: patientInfo.chiefComplaint },
      { label: '诊断', value: patientInfo.diagnosis },
      { label: '现病史', value: patientInfo.presentIllness },
      { label: '既往史', value: patientInfo.pastIllness },
      { label: '家族史', value: patientInfo.familyIllness },
      { label: '过敏史', value: patientInfo.allergy },
      { label: '个人史', value: patientInfo.personalHistory },
      { label: '月经婚育史', value: patientInfo.menstrualMaritalHistory },
      { label: '望闻问切', value: patientInfo.inspectionAuscultation },
      { label: '辩证', value: patientInfo.syndromeDifferentiation },
      { label: '治法', value: patientInfo.treatmentMethod },
      { label: '体格检查', value: patientInfo.physicalExamination },
    ]
    medicalFieldRows.forEach(row => {
      if (!hasContent(row.value)) return
      html += `<div style="display: flex; margin-bottom: ${
        medicalFieldSpacing
      }px; line-height: ${medicalFieldLineHeight}px;"><span style="width: 80px; flex: 0 0 80px; font-weight: bold;">${
        row.label
      }：</span><span style="min-width: 0; flex: 1;">${String(row.value).replace(
        /\n/g,
        '<br/>'
      )}</span></div>`
    })
    html += `</div>`

    // 处方内容
    if (
      showTreatmentContent &&
      detailData.prescriptionList &&
      detailData.prescriptionList.length > 0
    ) {
      detailData.prescriptionList.forEach((prescription: any) => {
        if (prescription.prescriptionType === 1) {
          // 中药处方
          html += `<div style="border-top: 1px dashed #999; padding-top: ${
            config.lineSpacing || 10
          }px; margin-bottom: ${config.lineSpacing || 10}px;">`
          if (prescription.name) {
            html += `<div style="font-weight: bold; margin-bottom: ${
              config.lineSpacing || 10
            }px;">${prescription.name}</div>`
          }
          html += `<div style="display: flex; flex-wrap: wrap;">`
          if (prescription.drugList) {
            prescription.drugList.forEach((drug: any) => {
              const singleDoseWeight = getChineseMedicineSingleDoseWeight(
                drug,
                prescription
              )
              html += `<span style="margin-right: 20px; margin-bottom: ${
                config.lineSpacing || 10
              }px;">`
              if (checkMed('商品名'))
                html += `<span>${drug.drugName || ''}</span> `
              if (singleDoseWeight) {
                html += `<span>${singleDoseWeight}</span>`
              } else if (drug.specification) {
                html += `<span>${drug.specification}</span>`
              } else if (drug.quantity) {
                html += `<span>${drug.quantity}${drug.unit || ''}</span>`
              }
              html += `</span>`
            })
          }
          html += `</div>`
          html += `<div style="margin-top: ${config.lineSpacing || 10}px;">`
          html += formatChineseMedicineDoseUsage(prescription)
          html += `</div>`
          html += `</div>`
        } else if (prescription.prescriptionType === 3) {
          // 贴敷处方
          html += `<div style="border-top: 2px dashed #000; padding-top: ${
            config.lineSpacing || 10
          }px; margin-bottom: ${config.lineSpacing || 10}px;">`
          if (prescription.name) {
            html += `<div style="font-weight: bold; margin-bottom: ${
              config.lineSpacing || 10
            }px;">${prescription.name}</div>`
          }
          prescription.itemList?.forEach((item: any) => {
            if (item.drugList) {
              html += `<div style="display: flex; flex-wrap: wrap; margin-bottom: 5px;">`
              item.drugList.forEach((drug: any) => {
                html += `<span style="margin-right: 15px;">`
                if (checkMed('商品名')) {
                  html += `${drug.drugName || ''} `
                }
                if (drug.specification) {
                  html += `(${drug.specification}) `
                }
                html += `${drug.quantity || 0}${drug.unit || ''}`
                html += `</span>`
              })
              html += `</div>`
            }
            if (item.acupointsList && item.acupointsList.length > 0) {
              const acupoints = item.acupointsList
                .map((a: any) => a.acupointName)
                .join('、')
              html += `<div style="margin-bottom: 5px;">穴位：${acupoints}</div>`
            }
            let usageText = item.frequencyUnit || ''
            if (item.times) usageText += `，${item.times}次`
            if (checkMed('天数') && item.days) usageText += `，${item.days}天`

            if (usageText) {
              html += `<div style="margin-bottom: 5px;">用法：${usageText}</div>`
            }
          })
          html += `</div>`
        } else if (prescription.prescriptionType === 4) {
          // 治疗理疗项目
          const treatmentList = Array.isArray(prescription.drugList)
            ? prescription.drugList
            : []
          if (treatmentList.length === 0) return

          html += `<div style="border-top: 2px dashed #000; padding-top: ${
            config.lineSpacing || 10
          }px; margin-bottom: ${config.lineSpacing || 10}px;">`
          if (prescription.name) {
            html += `<div style="font-weight: bold; margin-bottom: ${
              config.lineSpacing || 10
            }px;">${prescription.name}</div>`
          }

          getMedicalTreatmentPrintItems(treatmentList).forEach((item: any) => {
            const usageText = joinDisplayParts([
              item.frequencyUnit,
              item.days ? `${item.days}天` : '',
              item.quantity ? `共${item.quantity}${item.unit || ''}` : '',
            ])

            html += `<div style="margin-bottom: ${
              config.lineSpacing || 10
            }px;"><div style="display: flex; justify-content: space-between; gap: 12px;">`
            html += `<span style="flex: 1; min-width: 0; overflow-wrap: anywhere; padding-left: ${
              item.__packageChild ? 16 : 0
            }px;">${
              item.__packageChild ? item.itemName || '' : item.drugName || ''
            }</span>`
            html += `<span style="text-align: right; min-width: 0; max-width: 65%; overflow-wrap: anywhere;">${escapePrintHtml(usageText)}</span>`
            html += `</div>`
            if (hasContent(item.remark)) {
              html += `<div class="medical-item-remark" style="margin-top: 3px; color: var(--secondary-text, #666); white-space: pre-wrap; overflow-wrap: anywhere; word-break: break-word;">备注：${escapePrintHtml(item.remark)}</div>`
            }
            html += `</div>`
          })
          html += `</div>`
        } else if (prescription.prescriptionType !== 4) {
          // 西药/成药 (排除治疗项目 type 4)
          html += `<div style="border-top: 1px dashed #999; padding-top: ${
            config.lineSpacing || 10
          }px; margin-bottom: ${config.lineSpacing || 10}px;">`
          if (prescription.name) {
            html += `<div style="font-weight: bold; margin-bottom: ${
              config.lineSpacing || 10
            }px;">${prescription.name}</div>`
          }
          if (prescription.drugList) {
            prescription.drugList.forEach((drug: any) => {
              const dosageText = formatWesternMedicineUsageDosage(drug)
              html += `<div style="display: flex; justify-content: space-between; margin-bottom: ${
                config.lineSpacing || 10
              }px;">`
              html += `<div style="flex: 1;">`
              if (checkMed('商品名'))
                html += `<span>${drug.drugName || ''}</span> `
              if (checkMed('规格'))
                html += `<span>${
                  drug.specification ? `(${drug.specification})` : ''
                }</span>`
              html += `</div>`
              html += `<div style="flex: 1; text-align: right;">`
              html += `×${drug.quantity || 0} ${drug.unit || ''} ${dosageText} `
              html += `${drug.frequencyUnit || ''}`
              if (checkMed('天数') && drug.days)
                html += ` <span>${drug.days}天</span>`
              html += `</div>`
              html += `</div>`
              if (hasContent(drug.remark)) {
                html += `<div class="medical-item-remark" style="margin-bottom: ${config.lineSpacing || 10}px; color: var(--secondary-text, #666); white-space: pre-wrap; overflow-wrap: anywhere; word-break: break-word;">备注：${escapePrintHtml(drug.remark)}</div>`
              }
            })
          }
          html += `</div>`
        }
      })
    }

    // 医嘱
    if (config.medicalAdvice && patientInfo.medicalAdvice) {
      html += `<div style="border-top: 2px dashed #000; padding-top: ${
        config.lineSpacing || 10
      }px; margin-bottom: ${config.lineSpacing || 10}px;">`
      html += `<div style="display: flex;">`
      html += `<div style="width: 60px; font-weight: bold;">医嘱：</div>`
      html += `<div style="flex: 1;">${patientInfo.medicalAdvice.replace(
        /\n/g,
        '<br/>'
      )}</div>`
      html += `</div>`
      html += `</div>`
    }

    html += `</div></td></tr></tbody></table>
    <!-- 页尾 -->
    <div class="medical-record-footer" style="margin-top: auto;">
      <div style="border-top: 1px solid #000; padding-top: ${
        config.lineSpacing || 10
      }px; display: flex; flex-wrap: wrap; justify-content: space-between; margin-bottom: ${
      config.lineSpacing || 10
    }px;">`
    html += `<span style="margin-right: 20px;">医生：${patientInfo.doctorName}</span>`
    if (checkFoot('金额'))
      html += `<span style="margin-right: 20px;">金额：${patientInfo.totalPrice}元</span>`

    if (checkFoot('电话'))
      html += `<span style="margin-right: 20px;">电话：${
        detailData.contactPhone || ''
      }</span>`
    if (checkFoot('地址'))
      html += `<span style="margin-right: 20px;">地址：${
        detailData.clinicAddress || ''
      }</span>`
    html += `</div>`

    if (checkFoot('打印时间')) {
      html += `<div style="margin-bottom: ${
        config.lineSpacing || 10
      }px;">打印时间：${custFormatDate(
        new Date(),
        'YYYY-MM-DD HH:mm:ss'
      )}</div>`
    }

    // 页尾备注
    if (config.footerRemark) {
      html += `<div style="font-size: ${
        (config.fontSize || 14) - 2
      }px; color: #666;">${config.footerRemark}</div>`
    }

    html += `</div>${
      isSmallPaper ? '<div class="bottom-space"></div>' : ''
    }`

    executePrint(
      html,
      paperSize,
      '病历',
      getHospitalName(config),
      false,
      isSmallPaper ? undefined : '0 10mm 5mm 10mm'
    )
  } catch (error) {
    console.error('打印失败:', error)
    ElMessage.error('打印失败')
  }
}

// 打印诊断证明书
export const printDiagnosisCertificate = async (
  medicalId: string | number
) => {
  try {
    const [configRes, detailRes, fieldSettingRes] = await Promise.all([
      printDiagnosisCertificateDetail(),
      getDiagnosisCertificatePrint({
        id: medicalId,
      }),
      caseItemSwitchGet(),
    ])
    if (configRes.code !== 0 || !configRes.data) {
      ElMessage.error('获取诊断证明书打印配置失败')
      return
    }
    if (detailRes.code !== 0 || !detailRes.data) {
      ElMessage.error('获取病历详情失败')
      return
    }
    if (fieldSettingRes.code !== 0 || !fieldSettingRes.data) {
      ElMessage.error('获取病历字段配置失败')
      return
    }

    const config = configRes.data
    const detail = detailRes.data
    const fieldSetting = fieldSettingRes.data
    const showPatient = (field: string) =>
      config.patientInfo?.includes(field)
    const formatDate = (value: any) => {
      if (!value) return ''
      return custFormatDate(
        new Date(value),
        config.visitDateDisplay === 2 ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'
      )
    }
    const phone = detail.mobile || ''
    const mobile =
      config.informationDesensitization?.includes('1') && phone.length >= 7
        ? `${phone.slice(0, 3)}****${phone.slice(-4)}`
        : phone
    // 固定显示姓名、性别、年龄
    const nameAgeText = [
      detail.userName,
      detail.sex === 1 ? '男' : detail.sex === 2 ? '女' : '',
      formatDiagnosisCertificateAge(detail)
    ].filter(Boolean).join(' ')
    const patientItems = [
      `姓名：${escapePrintHtml(nameAgeText)}`,
      showPatient('科室') &&
        `科室：${escapePrintHtml(detail.departmentsName)}`,
      showPatient('诊号') &&
        `诊号：${escapePrintHtml(getPatientVisitNumber(detail))}`,
      showPatient('证件') && `证件：${escapePrintHtml(detail.cardNo)}`,
      showPatient('手机号') && `手机：${escapePrintHtml(mobile)}`,
      `日期：${escapePrintHtml(formatDate(detail.visitDate || detail.createTime))}`,
    ].filter(Boolean)
    const contentRows = [
      ['chiefComplaint', '主诉', detail.chiefComplaint],
      ['onsetDate', '发病日期', formatDate(detail.onsetDate)],
      ['presentIllness', '现病史', detail.presentIllness],
      ['pastIllness', '既往史', detail.pastIllness],
      ['familyIllness', '家族史', detail.familyIllness],
      ['allergy', '过敏史', detail.allergy],
      ['personalHistory', '个人史', detail.personalHistory],
      ['menstrualMaritalHistory', '月经婚育史', detail.menstrualMaritalHistory],
      ['inspectionAuscultation', '望闻问切', detail.inspectionAuscultation],
      ['diagnosis', '诊断', detail.diagnosis],
      ['syndromeDifferentiation', '辨证', detail.syndromeDifferentiation],
      ['treatmentMethod', '治法', detail.treatmentMethod],
      ['formula', '方剂', detail.formula],
      ['medicinePrescription', '方药', detail.medicinePrescription],
      ['physicalExamination', '体格检查', detail.physicalExamination],
    ].filter(([field]) => fieldSetting[field] === true)
    const printTime = custFormatDate(new Date(), 'YYYY-MM-DD HH:mm:ss')
    const html = `
      <div class="print-wrapper" style="width:100%;height:190mm;min-height:190mm;box-sizing:border-box;font-size:14px;line-height:1.65;color:#111;">
        <div class="print-main">
          ${(config.settingName || []).map((name: string) => `<div style="text-align:center;font-size:18px;font-weight:600;">${escapePrintHtml(name)}</div>`).join('')}
          <div style="text-align:center;font-size:22px;font-weight:700;margin:5px 0 14px;">诊断证明书</div>
          <div style="display:flex;flex-wrap:wrap;gap:6px 22px;padding-bottom:8px;border-bottom:1px solid #333;">${patientItems.map(item => `<span>${item}</span>`).join('')}</div>
          <div style="padding:8px 0;">${contentRows.map(([, label, value]) => `<div style="display:flex;align-items:flex-start;margin:4px 0;min-height:23px;"><b style="width:88px;flex:0 0 88px;white-space:nowrap;">${label}：</b><span style="min-width:0;flex:1;white-space:pre-wrap;word-break:break-word;">${escapePrintHtml(value)}</span></div>`).join('')}</div>
        </div>
        <div class="print-footer" style="display:flex;justify-content:space-between;gap:16px;padding-top:8px;border-top:1px solid #333;white-space:nowrap;break-inside:avoid;page-break-inside:avoid;">
          <span>医生：${escapePrintHtml(detail.doctorName)}</span>
          <span>盖章：</span>
          <span>打印时间：${escapePrintHtml(printTime)}</span>
        </div>
      </div>`
    executePrint(
      html,
      getPaperSize({ paperSize: 'A5' }, 'A5'),
      '诊断证明书',
      getHospitalName(config),
      false,
      '5mm 7mm'
    )
  } catch (error) {
    console.error('打印诊断证明书失败:', error)
    ElMessage.error('打印诊断证明书失败')
  }
}

// 打印处方
export const printPrescription = async (
  medicalId: string | number,
  printConfigParams?: PrintConfigParams,
  output?: PrintOutputOptions
) => {
  try {
    const res = await printPrescriptionDetail()
    if (res.code !== 0 || !res.data) {
      if (output) throw new Error('获取打印配置失败')
      ElMessage.error('获取打印配置失败')
      return
    }
    const config = res.data
    const showChinesePrescriptionNumber = readChinesePrescriptionNumber(config.id)
    const paperSize = getPaperSize(config, 'A4')
    const isSmallPaper =
      paperSize.width === '58mm' || paperSize.width === '80mm'
    const fixedPaperContentMinHeight =
      !isSmallPaper && paperSize.height && paperSize.height !== 'auto'
        ? 'calc(100vh - 12mm)'
        : 'auto'

    const detailRes = await getPrescriptionPrint({
      id: medicalId,
    })
    if (detailRes.code !== 0) {
      if (output) throw new Error('获取病历详情失败')
      ElMessage.error('获取病历详情失败')
      return
    }
    const detailData = detailRes.data
    if (output && !detailData?.prescriptionList?.length) return
    await fillChineseMedicineCabinetNumbers(detailData.prescriptionList || [])
    const patientInfo = {
      userName: detailData.userName || '',
      sex: detailData.sex == 1 ? '男' : detailData.sex == 2 ? '女' : '',
      ageYear: detailData.ageYear || '',
      visitNumber: getPatientVisitNumber(detailData),
      cardNo: detailData.cardNo || '',
      birthday: detailData.birthDate || detailData.birthday || '',
      weight: detailData.weight || '',
      marriage: detailData.marriage || '',
      nation: detailData.nation || '',
      work: detailData.work || '',
      workUnit: detailData.workUnit || '',
      address: detailData.address || '',
      mobile: detailData.mobile || '',
    }

    // 辅助函数：判断是否显示签名行或成药简写等
    const checkPres = (field: string) =>
      config.prescriptionStandard?.includes(field)
    // 辅助函数：判断页尾内容是否需要显示
    const showFoot = (field: string) => config.footerContent?.includes(field)
    const checkPat = (field: string) => config.patientInfo?.includes(field)
    const hasContent = (value: any) =>
      value !== undefined && value !== null && String(value).trim() !== ''
    const formatPhone = (phone: string) => {
      if (!phone || config.phoneDisplay === 3) return ''
      if (config.phoneDisplay === 2) {
        return phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
      }
      return phone
    }

    const checkXi = (field: string) => config.westernMedicine?.includes(field)
    const checkPlaster = (field: string) =>
      config.plasterPrescription?.includes(field)
    const checkOther = (field: string) =>
      config.otherPrescription?.includes(field)
    const checkChin = (field: string) => config.chineseMedicine?.includes(field)
    const checkAdvice = (field: string) => config.medicalAdvice?.includes(field)
    const chineseMedicineLayout =
      Number(config.chineseMedicineLayout) === 4 ? 4 : 3
    const baseFontSize = Math.max(Number(config.fontSize) || 12, 14)
    const baseLineSpacing = Math.max(Number(config.lineSpacing) || 2, 2)
    const contentLineHeight = baseFontSize + Math.max(baseLineSpacing - 2, 2)
    const sectionGap = Math.max(baseLineSpacing, 2)
    const chineseDrugRowGap = Math.max(baseLineSpacing / 2, 2)
    const chineseDrugColumnGap = chineseMedicineLayout === 4 ? '6mm' : '10mm'
    const plasterDrugColumnGap = '8mm'
    const amountDisplay = Number(config.amountDisplay)
    const isCharged = Number(detailData?.recordStatus) >= 4000
    // 就诊单处于部分收费时，recordStatus 仍可能是待收费状态，
    // 但处方/费用明细已经带有 chargeStatus 和实收金额。打印金额必须
    // 按明细状态选择 payPrice，否则门诊总金额会被汇总成 0 或总价。
    const hasPaidAmount = (item: any) =>
      isCharged || [1, 2, 3, 4].includes(Number(item?.chargeStatus))
    const getPrintedAmount = (item: any) => {
      if (!item) return 0

      const amount = getNumericValue(
        hasPaidAmount(item) ? item?.payPrice : item?.totalPrice
      )
      if (amount > 0) return amount

      // 部分收费/历史快照可能只返回明细金额，处方主项金额为空或为 0。
      // 继续按同一收费状态从 itemList/drugList 汇总，保留未收费时的总价规则。
      const details = Array.isArray(item?.itemList)
        ? item.itemList
        : Array.isArray(item?.drugList)
        ? item.drugList
        : []
      if (!details.length) return amount

      return details.reduce((sum: number, detail: any) => {
        const detailAmount = getNumericValue(
          hasPaidAmount(item) || hasPaidAmount(detail)
            ? detail?.payPrice
            : detail?.totalPrice
        )
        if (detailAmount > 0) return sum + detailAmount

        const unitPrice = getNumericValue(detail?.unitPrice)
        const quantity = getNumericValue(detail?.quantity)
        return sum + (unitPrice > 0 && quantity > 0 ? unitPrice * quantity : 0)
      }, 0)
    }

    const getPlasterItemName = (cf: any, item: any, index: number) => {
      const typeName =
        Number(cf?.plasterType ?? item?.plasterType) === 2 ? '中药贴敷' : '贴敷'
      return `${index + 1}.${typeName} / 穴位`
    }
    const getPlasterDrugDoseText = (drug: any) => {
      const dose = checkPlaster('克数')
        ? drug?.dosage || (drug?.quantity ?? '')
        : drug?.quantity ?? ''
      const unit = drug?.unit || ''

      if (dose === '' && !unit) return ''
      return `${dose}${unit}`
    }
    const getPlasterUsageText = (item: any) => {
      const acupointCount = Array.isArray(item?.acupointsList)
        ? item.acupointsList.length
        : 0
      const times = Number(item?.times || item?.quantity || 0)
      const frequencyText = item?.frequencyUnit || ''
      const everyTimeText = acupointCount ? `，每次${acupointCount}穴` : ''
      const totalAcupointText =
        acupointCount && times ? `，共${acupointCount * times}穴` : ''
      const totalTimesText = times ? `共${times}次` : '共0次'
      return `用法：${totalTimesText}${
        frequencyText ? `，${frequencyText}` : ''
      }${everyTimeText}${totalAcupointText}`
    }
    const checkHumanisticCare = (prescription: any) => {
      const type = Number(prescription?.prescriptionType)
      if (type === 1) return checkChin('人文关怀')
      if (type === 2) return checkXi('人文关怀')
      if (type === 3) return checkPlaster('人文关怀')
      if (type === 5) return checkOther('人文关怀')
      return false
    }
    const getHumanisticCareHtml = (prescriptionList: any[]) =>
      (prescriptionList || [])
        .filter(
          (prescription: any) =>
            prescription?.prescriptionType !== 4 &&
            checkHumanisticCare(prescription) &&
            hasContent(prescription?.humanisticCare)
        )
        .map((prescription: any) => {
          return `<div style="white-space: pre-wrap;">${String(
            prescription.humanisticCare
          ).replace(/\n/g, '<br/>')}</div>`
        })
        .join('') || ''

    const buildPrescriptionsHtml = (prescriptionList: any[]) =>
      (prescriptionList || [])
        ?.filter((cf: any) => cf.prescriptionType !== 4)
        ?.map((cf: any) => {
          if (cf.prescriptionType === 1) {
            // 中药处方
            let html = `<div style="border-top: 1px solid #000; padding-top: 2px; margin-bottom: 2px;">`
            html += `<div style="font-weight: bold; font-size: ${
              baseFontSize + 3
            }px; margin-bottom: 2px;">Rp</div>`
            // 药品列表
            html += `<div style="display: grid; grid-template-columns: repeat(${chineseMedicineLayout}, minmax(0, 1fr)); column-gap: ${chineseDrugColumnGap}; row-gap: ${chineseDrugRowGap}px; margin-top: 2px; margin-bottom: 2px;">`
            cf.drugList?.forEach((drug: any) => {
              html += `<div style="box-sizing: border-box; min-width: 0; padding: 2px 0; line-height: ${contentLineHeight}px; break-inside: avoid;">`
              // 单个药品价格预览
              if (
                config.chineseMedicine?.includes('单剂价格') ||
                config.chineseMedicine?.includes('总价')
              ) {
                html += `<div style="font-size: 0.8em; color: #666; line-height: 1.4; margin-bottom: 2px;">`
                if (config.chineseMedicine.includes('单剂价格')) {
                  html += `¥${drug.unitPrice || 0}/${drug.unit || 'g'}`
                }
                if (
                  config.chineseMedicine.includes('单剂价格') &&
                  config.chineseMedicine.includes('总价')
                ) {
                  html += `，`
                }
                if (config.chineseMedicine.includes('总价')) {
                  html += `共¥${getPrintedAmount(drug)}`
                }
                html += `</div>`
              }
              let drugName = `${drug.drugName || ''}`
              if (checkChin('柜号') && (drug.cabinetNumber || drug.cabinetNo)) {
                drugName += `(${drug.cabinetNumber || drug.cabinetNo})`
              }
              if (
                checkChin('单个药品调剂方法') &&
                (drug.dispensing || drug.usage)
              ) {
                drugName += `(${drug.dispensing || drug.usage})`
              }
              // 克数显示
              let weights = []
              if (checkChin('单剂克数')) {
                const q = parseFloat(drug.quantity) || 0
                const doses = parseFloat(cf.quantity) || 1
                const singleWeightVal =
                  drug.dosage && !isNaN(parseFloat(drug.dosage))
                    ? parseFloat(drug.dosage)
                    : q / doses

                if (singleWeightVal > 0) {
                  weights.push(
                    parseFloat(singleWeightVal.toFixed(2)).toString() +
                      (drug.unit || 'g')
                  )
                }
              }
              if (checkChin('总剂克数')) {
                if (drug.quantity && parseFloat(drug.quantity) > 0) {
                  const totalW = parseFloat(
                    parseFloat(drug.quantity).toFixed(2)
                  )
                  const totalStr = totalW.toString() + (drug.unit || 'g')
                  // 如果已经有了单剂克数且两者相同，则不重复添加
                  if (weights.length === 0 || weights[0] !== totalStr) {
                    weights.push(totalStr)
                  }
                }
              }
              html += `<div style="display: flex; justify-content: space-between; align-items: baseline; gap: 4px; white-space: nowrap; padding-right: 12px;">`
              html += `<span style="font-weight: 600;">${drugName}</span>`
              if (weights.length > 0) {
                html += `<span style="font-weight: 600;">${weights.join(
                  '/'
                )}</span>`
              }
              html += `</div>`
              html += `</div>`
            })
            html += `</div>`
            const totalWeight =
              cf.drugList?.reduce(
                (sum: number, d: any) => sum + (parseFloat(d.quantity) || 0),
                0
              ) || 0
            html += `<div style="margin-top: ${Math.max(
              sectionGap,
              2
            )}px;">用法：共${cf.quantity || 0}剂，${cf.drugList?.length || 0}味`
            if (checkChin('单剂克数')) {
              html += `，单剂${parseFloat(
                (totalWeight / (cf.quantity || 1)).toFixed(2)
              )}g`
            }
            if (checkChin('总数')) {
              html += `，总重${parseFloat(totalWeight.toFixed(2))}g`
            }
            html += `</div>`
            const usageDetailText = formatChineseMedicineUsageDetail(cf)
            if (usageDetailText) {
              html += `<div style="margin-top: ${Math.max(
                baseLineSpacing - 2,
                2
              )}px;">${usageDetailText}</div>`
            }
            if (checkAdvice('加工信息') && cf.processing) {
              const totalBags =
                (cf.processing.quantity || 0) * (cf.processing.drugDosage || 1)
              const processingFee = getPrintedAmount(cf.processing)
              const processingText = `${cf.processing.processingName || ''}，${
                cf.processing.drugDosage || ''
              }剂煎${totalBags}袋`
              const processingNotesText = cf.processing.processingNotes
                ? `，加工备注：${cf.processing.processingNotes}`
                : ''
              const processingFeeText =
                amountDisplay === 4
                  ? ''
                  : `，加工费：${formatMoney(processingFee)}元`
              html += `<div style="border-top: 1px dashed #000; padding-top: ${
                baseLineSpacing + 2
              }px; margin-top: ${sectionGap}px;">加工：${processingText}${processingNotesText}${processingFeeText}</div>`
            }
            html += `</div>`
            return html
          } else if (cf.prescriptionType === 3) {
            // 门诊和收费统一使用 itemList 贴敷分组结构。
            let html = `<div style="border-top: 1px solid #000; padding-top: 4px; margin-bottom: ${
              config.lineSpacing || 5
            }px;">`
            html += `<div style="font-weight: bold; font-size: ${
              baseFontSize + 3
            }px; margin-bottom: 2px;">Rp</div>`

            cf.itemList?.forEach((item: any, index: number) => {
              html += `<div style="margin-bottom: ${
                sectionGap + 2
              }px; break-inside: avoid;">`
              html += `<div style="display: flex; justify-content: space-between; align-items: baseline; gap: 8px; font-weight: bold; margin-bottom: 2px;">`
              html += `<span>${getPlasterItemName(cf, item, index)}</span>`
              if (item.times) {
                html += `<span style="white-space: nowrap;">×${item.times}</span>`
              }
              html += `</div>`

              const drugList = Array.isArray(item?.drugList)
                ? item.drugList
                : []
              if (drugList.length > 0) {
                html += `<div style="display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); column-gap: ${plasterDrugColumnGap}; row-gap: 2px; margin: 2px 0;">`
                drugList.forEach((drug: any) => {
                  const drugParts = []
                  if (checkPlaster('药品名称') && drug?.drugName) {
                    let drugName = drug.drugName
                    if (checkPlaster('规格') && drug.specification) {
                      drugName += `(${drug.specification})`
                    }
                    if (checkPlaster('单价')) {
                      drugName += ` ¥${formatMoney(drug.unitPrice || 0)}`
                    }
                    drugParts.push(drugName)
                  }
                  const doseText = getPlasterDrugDoseText(drug)
                  if (doseText) drugParts.push(doseText)

                  html += `<div style="display: flex; justify-content: space-between; align-items: baseline; gap: 4px; min-width: 0; line-height: ${contentLineHeight}px; white-space: nowrap;">`
                  html += `<span>${drugParts[0] || ''}</span>`
                  html += `<span>${drugParts[1] || ''}</span>`
                  html += `</div>`
                })
                html += `</div>`
              }

              if (
                checkPlaster('穴位') &&
                item.acupointsList &&
                item.acupointsList.length > 0
              ) {
                const acupoints = item.acupointsList
                  .map((a: any) => a.acupointName)
                  .filter(Boolean)
                  .join('、')
                html += `<div style="margin-top: 2px;">穴位：${acupoints}</div>`
              }

              html += `<div style="margin-top: 2px;">${getPlasterUsageText(
                item
              )}</div>`
              if (hasContent(item?.remark)) {
                html += `<div style="margin-top: 3px; color: var(--secondary-text, #666); white-space: pre-wrap; overflow-wrap: anywhere; word-break: break-word;">备注：${escapeHtml(
                  String(item.remark)
                )}</div>`
              }
              html += `</div>`
            })

            if (checkPlaster('总价')) {
              html += `<div style="margin-top: 5px; text-align: right;">金额：${formatMoney(
                getPrintedAmount(cf)
              )}元</div>`
            }
            html += `</div>`
            return html
          } else if (cf.prescriptionType === 4) {
            // 治疗项目
            let html = `<div style="border-top: 1px dashed #000; padding-top: 5px; margin-bottom: ${
              config.lineSpacing || 5
            }px;">`
            cf.drugList?.forEach((drug: any) => {
              html += `<div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
          <span>${drug.drugName || ''}</span>
          <span>×${drug.quantity || 1}${
                drug.days && drug.days > 1 ? ` ${drug.days}次` : ''
              } ￥${formatMoney(drug.unitPrice || 0)}元</span>
        </div>`
            })
            html += `<div style="margin-top: 5px; text-align: right;">金额：${formatMoney(
              getPrintedAmount(cf)
            )}元</div>`
            html += `</div>`
            return html
          } else if (cf.prescriptionType === 5) {
            // 其他处方
            let html = `<div style="border-top: 1px dashed #000; padding-top: 5px; margin-bottom: ${
              config.lineSpacing || 5
            }px;">`
            html += `<div style="font-weight: bold;">Rp</div>`
            cf.drugList?.forEach((drug: any) => {
              html += `<div style="margin-bottom: 3px;">
                <div style="display: flex; justify-content: space-between;">
                  <span>`
              if (checkOther('商品名称')) html += drug.drugName || ''
              if (checkOther('规格') && drug.specification)
                html += `(${drug.specification})`
              if (checkOther('单价'))
                html += ` (¥${formatMoney(drug.unitPrice || 0)})`
              html += `</span>
                  <span>×${drug.quantity || ''} ${drug.unit || ''}`
              if (checkOther('天数') && drug.days) html += ` ${drug.days}天`
              html += `</span>
                </div>`
              const remarkText = String(drug?.remark || '').trim()
              if (checkOther('备注') && remarkText) {
                html += `<div style="margin-top: 3px; color: var(--secondary-text, #666); white-space: pre-wrap; overflow-wrap: anywhere; word-break: break-word;">备注：${escapeHtml(
                  remarkText
                )}</div>`
              }
              html += `
              </div>`
            })
            if (checkOther('总价')) {
              html += `<div style="margin-top: 2px; text-align: right;">金额：${formatMoney(
                getPrintedAmount(cf)
              )}元</div>`
            }
            html += `</div>`
            return html
          } else {
            // 成药处方 (Type 2)
            let html = `<div style="border-top: 1px dashed #000; padding-top: 2px; margin-bottom: ${Math.max(
              config.lineSpacing || 5,
              2
            )}px;">`
            html += `<div style="font-weight: bold;">Rp</div>`
            cf.drugList?.forEach((drug: any) => {
              const showManufacturer = checkXi('厂家') && drug.manufacturer
              html += `<div style="margin-bottom: 3px;">
                <div style="display: flex; justify-content: space-between;">
                  <span>`
              if (checkXi('商品名')) html += drug.drugName || ''
              if (checkXi('规格') && drug.specification)
                html += `(${drug.specification})`
              if (checkXi('单价'))
                html += ` (¥${formatMoney(drug.unitPrice || 0)})`
              html += `</span>
                  <span>×${drug.quantity || ''} ${drug.unit || ''}`
              if (checkXi('天数') && drug.days) html += ` ${drug.days}天`
              html += `</span>
                </div>`
              if (showManufacturer) {
                html += `<div style="font-size: 0.9em; color: #666;">厂家：${drug.manufacturer}</div>`
              }
              const usageText = formatWesternMedicineUsage(drug)
              const remarkText = String(drug?.remark || '').trim()
              const westernDetailParts = []
              if (checkXi('用法') && usageText) {
                westernDetailParts.push(`用法：${usageText}`)
              }
              if (checkXi('备注') && remarkText) westernDetailParts.push(`备注：${remarkText}`)
              if (westernDetailParts.length > 0) {
                html += `<div style="display: flex; flex-direction: column; gap: 2px; margin-top: 3px; min-width: 0;">${westernDetailParts
                  .map(part => `<div style="${part.startsWith('备注：') ? 'color: var(--secondary-text, #666);' : ''} white-space: pre-wrap; overflow-wrap: anywhere; word-break: break-word;">${escapeHtml(part)}</div>`)
                  .join('')}</div>`
              }
              if (checkXi('总价')) {
                html += `<div style="text-align: right;">¥${formatMoney(
                  getPrintedAmount(drug)
                )}</div>`
              }
              html += `</div>`
            })
            html += `<div style="margin-top: 2px; text-align: right;">合计：${formatMoney(
              getPrintedAmount(cf)
            )}元</div>`
            html += `</div>`
            return html
          }
        })
        .join('') || ''

    const printablePrescriptionList = (
      detailData.prescriptionList || []
    ).filter((cf: any) => cf?.prescriptionType !== 4)
    if (!printablePrescriptionList.length) {
      if (output) throw new Error('暂无可打印处方')
      ElMessage.error('暂无可打印处方')
      return
    }

    let visitAmount = { total: 0, rows: [] as Array<{ label: string; amount: number }> }
    if (amountDisplay === 3 || amountDisplay === 4) {
      const chargeRes = await getChargeDetail(medicalId)
      if (chargeRes.code !== 0 || !Array.isArray(chargeRes.data?.chargesList)) {
        throw new Error('获取门诊收费明细失败')
      }
      visitAmount = summarizePrescriptionCharges(chargeRes.data.chargesList)
    }

    const getPrescriptionProcessList = (prescription: any) => {
      const prescriptionIds = [prescription?.id, prescription?.prescriptionId]
        .filter(id => id !== undefined && id !== null && id !== '')
        .map(id => String(id))
      if (!prescriptionIds.length) return []

      return getMergedProcessingList(detailData).filter((process: any) => {
        const processPrescriptionId =
          process?.prescriptionsId ?? process?.prescriptionId
        return (
          processPrescriptionId !== undefined &&
          processPrescriptionId !== null &&
          prescriptionIds.includes(String(processPrescriptionId))
        )
      })
    }

    const printTimeText = showFoot('打印时间')
      ? `打印时间：${custFormatDate(new Date(), 'YYYY-MM-DD HH:mm:ss')}`
      : ''
    const pharmacistFooterItems = getEnabledPrescriptionSignatureLines(
      config.signatureLine
    )
    const patientHeaderItems: string[] = []
    const appendPatientField = (
      label: string,
      value: any,
      options: { fullWidth?: boolean } = {}
    ) => {
      if (!hasContent(value)) return
      patientHeaderItems.push(
        `<span style="${
          options.fullWidth ? 'width: 100%;' : ''
        }">${label}：${value}</span>`
      )
    }
    const visitStatusText =
      detailData.visitStatus == 1
        ? '初诊'
        : detailData.visitStatus == 2
        ? '复诊'
        : ''

    // 固定显示姓名、性别、年龄
    const ageText = [
      patientInfo.ageYear ? `${patientInfo.ageYear}岁` : '',
      patientInfo.ageMonth ? `${patientInfo.ageMonth}月` : ''
    ].filter(Boolean).join('')
    const nameAgeText = [
      patientInfo.userName,
      patientInfo.sex,
      ageText
    ].filter(Boolean).join(' ')
    appendPatientField('姓名', nameAgeText)
    appendPatientField(
      '日期',
      custFormatDate(
        detailData.createTime,
        config.visitDate == 2 ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'
      )
    )
    if (checkPat('初复诊')) appendPatientField('初复诊', visitStatusText)
    if (checkPat('诊号')) appendPatientField('诊号', patientInfo.visitNumber)
    if (checkPat('身份证')) appendPatientField('身份证', patientInfo.cardNo)
    if (checkPat('出生日期'))
      appendPatientField('出生日期', patientInfo.birthday)
    if (checkPat('体重'))
      appendPatientField(
        '体重',
        patientInfo.weight ? `${patientInfo.weight}kg` : ''
      )
    if (checkPat('婚否')) appendPatientField('婚否', patientInfo.marriage)
    if (checkPat('民族')) appendPatientField('民族', patientInfo.nation)
    if (checkPat('职业')) appendPatientField('职业', patientInfo.work || '无')
    if (checkPat('工作单位'))
      appendPatientField('工作单位', patientInfo.workUnit || '无')
    if (checkPat('住址'))
      appendPatientField('住址', patientInfo.address)
    if (checkPat('科室'))
      appendPatientField('科室', detailData.departmentsName || '')
    if (checkPat('手机号'))
      appendPatientField('手机', formatPhone(patientInfo.mobile))

    const prescriptionSummaryRows: string[] = []
    if (checkPres('主诉') && hasContent(detailData.chiefComplaint)) {
      prescriptionSummaryRows.push(`主诉：${detailData.chiefComplaint}`)
    }
    prescriptionSummaryRows.push(`诊断：${detailData.diagnosis || ''}`)

    const buildPrescriptionAmountText = (
      prescription: any,
      processTotal: number
    ) => {
      if (amountDisplay === 1) return ''
      let amount = 0
      if (amountDisplay === 2) {
        amount = getPrintedAmount(prescription) + processTotal
      } else if (amountDisplay === 3 || amountDisplay === 4) {
        amount = visitAmount.total
      } else {
        amount = getPrintedAmount(detailData)
      }
      return `金额：${formatMoney(amount)}元`
    }

    const prescriptionAmountDetailHtml = (() => {
      if (amountDisplay !== 3) return ''
      const rows: string[] = []
      const addAmountRow = (label: string, amount: any) => {
        const value = Number(amount || 0)
        if (value <= 0) return
        rows.push(
          `<div style="display: flex; justify-content: space-between; margin-bottom: 2px;"><span>${label}</span><span>${formatMoney(
            value
          )}元</span></div>`
        )
      }

      visitAmount.rows.forEach(row => {
        addAmountRow(escapeHtml(row.label), row.amount)
      })

      if (!rows.length) return ''
      return `<div style="border-top: 1px dashed #000; padding-top: 5px; margin-top: 5px;">${rows.join(
        ''
      )}</div>`
    })()

    const buildPrescriptionPageHtml = (
      prescription: any,
      pageIndex: number
    ) => {
      const chinesePrescriptionIndex = printablePrescriptionList.filter((item: any) => item.prescriptionType === 1).indexOf(prescription)
      const prescriptionNumber = showChinesePrescriptionNumber && chinesePrescriptionIndex >= 0
        ? formatChinesePrescriptionNumber(chinesePrescriptionIndex + 1) : ''
      const pagePrescriptionList = [prescription]
      const pageProcessList = getPrescriptionProcessList(prescription)
      const pageProcessTotal = pageProcessList.reduce(
        (sum: number, p: any) => sum + getPrintedAmount(p),
        0
      )
      const prescriptions = buildPrescriptionsHtml(pagePrescriptionList)
      const humanisticCareHtml = getHumanisticCareHtml(pagePrescriptionList)
      const footerLeftText = `医生：${detailData.doctorName || ''}`
      const prescriptionAmountText = buildPrescriptionAmountText(
        prescription,
        pageProcessTotal
      )
      const pageBreakStyle =
        pageIndex < printablePrescriptionList.length - 1
          ? 'page-break-after: always; break-after: page;'
          : ''
      const pageTopPadding = pageIndex > 0 && !isSmallPaper ? '10mm' : '0'

      return `<div style="position: relative; width: 100%; font-size: ${baseFontSize}px; line-height: ${contentLineHeight}px; padding: ${pageTopPadding} 0 0 0; box-sizing: border-box; font-family: Arial, sans-serif; ${pageBreakStyle}">
      ${prescriptionNumber ? `<div style="position:absolute;top:${pageTopPadding};left:0;font-size:${baseFontSize}px;">${prescriptionNumber}</div>` : ''}
    <div style="display: flex; flex-direction: column; min-height: ${fixedPaperContentMinHeight};">
    <div style="flex: 1 1 auto;">
      ${
        config.settingName
          ? `<div style="text-align: center; font-weight: bold; font-size: ${
              baseFontSize + 6
            }px; margin-bottom: ${Math.max(baseLineSpacing - 4, 0)}px;">${
              Array.isArray(config.settingName)
                ? config.settingName.join(' ')
                : config.settingName
            }</div>`
          : ''
      }
      ${
        config.hospitalName
          ? `<div style="text-align: center; font-weight: bold; font-size: ${
              baseFontSize + 4
            }px; margin-bottom: ${Math.max(baseLineSpacing - 4, 0)}px;">${
              config.hospitalName
            }</div>`
          : ''
      }
      ${isPrescriptionDocumentTitleVisible(config) ? `<div style="text-align: center; font-weight: bold; font-size: ${
        baseFontSize + 3
      }px; margin-bottom: ${Math.max(
        baseLineSpacing - 4,
        0
      )}px; white-space: nowrap;">处方笺</div>` : ''}
      <div style="display: flex; flex-wrap: wrap; align-items: flex-start; gap: 8px 16px; margin-bottom: ${Math.max(baseLineSpacing - 4, 2)}px;">
        ${patientHeaderItems.join('')}
      </div>
      <div style="border-top: 1px solid #000; padding-top: ${Math.max(
        baseLineSpacing - 4,
        2
      )}px; margin-bottom: ${Math.max(
        baseLineSpacing - 4,
        2
      )}px;">${prescriptionSummaryRows
        .map((row: string) => `<div>${row}</div>`)
        .join('')}</div>
      ${prescriptions}
      ${prescriptionAmountDetailHtml}
      ${
        detailData.medicalAdvice
          ? `<div style="border-top: 1px dashed #000; padding-top: 2px; margin-top: 2px;">医嘱：<br/>${detailData.medicalAdvice.replace(
              /\n/g,
              '<br/>'
            )}</div>`
          : ''
      }
      ${
        checkPres('大额处方患者签字')
          ? `<div style="text-align: right; border-bottom: 1px solid #000; padding: 2px 0; margin: 2px 0;">大额处方意见：□ 同意 签名：<span style="display: inline-block; width: 150px; border-bottom: 1px solid transparent;"></span></div>`
          : ''
      }
      </div>
      <div style="margin-top: auto; break-inside: avoid; page-break-inside: avoid;">
        ${
          humanisticCareHtml
            ? `<div style="margin-bottom: ${Math.max(
                baseLineSpacing,
                4
              )}px;">${humanisticCareHtml}</div>`
            : ''
        }
        <div style="border-top: 1px solid #000; padding-top: 2px; margin-top: 2px;">
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;">
          <span style="white-space: nowrap;">${footerLeftText}</span>
          ${pharmacistFooterItems
            .map((item: any) => {
              return `<span style="white-space: nowrap;">${
                item.label
              }：${item.userName || ''}</span>`
            })
            .join('')}
          <span style="white-space: nowrap;">${prescriptionAmountText}</span>
        </div>
        ${printTimeText ? `<div style="margin-top: 2px;">${printTimeText}</div>` : ''}
      </div>
      ${
        config.footerRemark
          ? `<div style="margin-top: 2px; font-size: ${Math.max(
              baseFontSize - 2,
              10
            )}px; color: #666; white-space: pre-wrap; break-inside: avoid; page-break-inside: avoid;">${escapeHtml(
              config.footerRemark
            )}</div>`
          : ''
      }
    </div></div>${isSmallPaper ? '<div class="bottom-space"></div>' : ''}</div>`
    }

    const html = printablePrescriptionList
      .map((prescription: any, index: number) =>
        buildPrescriptionPageHtml(prescription, index)
      )
      .join('')

    return await executePrint(
      html,
      paperSize,
      '处方',
      getHospitalName(config),
      true,
      isSmallPaper ? undefined : '5mm 10mm 2mm 10mm',
      output
    )
  } catch (error) {
    if (output) throw error
    console.error('打印失败:', error)
    ElMessage.error('打印失败')
  }
}

type TreatmentPrintSource = 'medical' | 'execution'

// 打印治疗理疗单
export const printTreatment = async (
  medicalId: string | number,
  source: TreatmentPrintSource = 'medical',
  options: TreatmentPrintOptions = {},
  output?: PrintOutputOptions
) => {
  try {
    const res = await printTreatmentDetail()
    if (res.code !== 0 || !res.data) {
      if (output) throw new Error('获取打印配置失败')
      ElMessage.error('获取打印配置失败')
      return
    }
    const config = res.data
    const paperSize = getPaperSize(config, 'A4')
    const detailRes =
      source === 'execution'
        ? await getExecutionDetail({ id: medicalId })
        : await getTreatmentPrint({ id: medicalId })
    if (detailRes.code !== 0) {
      if (output) throw new Error('获取病历详情失败')
      ElMessage.error('获取病历详情失败')
      return
    }
    const normalizeExecutionTreatmentData = (rawData: any) => {
      const data = rawData || {}
      const records = Array.isArray(data?.list) ? data.list : []
      const firstRecord = records[0] || {}
      const treatmentItems = records.flatMap((record: any) =>
        (record.prescriptionList || []).map((item: any) => ({
          ...item,
          executor: item.executor || '',
        }))
      )

      return {
        ...data,
        createTime: data.applyDate || data.createTime || firstRecord.createTime,
        departmentsName:
          data.departmentName ||
          data.departmentsName ||
          firstRecord.departmentsName ||
          '',
        diagnosis:
          data.diagnosis ||
          firstRecord.diagnosis ||
          records.find((record: any) => record?.diagnosis)?.diagnosis ||
          '',
        doctorName: data.doctorName || firstRecord.doctorName || '',
        openUserName:
          data.openUserName ||
          firstRecord.openUserName ||
          firstRecord.prescriber ||
          data.doctorName ||
          firstRecord.doctorName ||
          '',
        chargeUserName:
          data.chargeUserName ||
          data.chargesUserName ||
          data.chargeName ||
          data.cashierUserName ||
          data.cashierName ||
          firstRecord.chargeUserName ||
          firstRecord.chargesUserName ||
          firstRecord.chargeName ||
          firstRecord.cashierUserName ||
          firstRecord.cashierName ||
          '',
        payPrice:
          pickNumericValue(
            data.payPrice,
            data.payAmount,
            data.receivableAmount,
            firstRecord.payPrice,
            firstRecord.payAmount,
            firstRecord.receivableAmount
          ) ?? data.payPrice,
        totalPrice:
          pickNumericValue(data.totalPrice, firstRecord.totalPrice) ??
          data.totalPrice,
        physioList: treatmentItems,
      }
    }

    const detailData =
      source === 'execution'
        ? normalizeExecutionTreatmentData(detailRes.data)
        : detailRes.data
    const isCharged = Number(detailData?.recordStatus) >= 4000
    const patientInfo = {
      userName: detailData.userName || '',
      sex: detailData.sex == 1 ? '男' : detailData.sex == 2 ? '女' : '',
      ageYear: detailData.ageYear || '',
      visitNumber: getPatientVisitNumber(detailData),
      cardNo: detailData.cardNo || '',
      mobile: detailData.mobile || '',
    }
    const showField = (field: string) => config.patientInfo?.includes(field)
    const showFoot = (field: string) => config.footerContent?.includes(field)
    const showTreat = (field: string) =>
      config.treatmentStandard?.includes(field)
    const formatTreatmentProgress = (drug: any) => {
      const usedCount = Number(drug?.usedQuantity ?? 0)
      const totalCount = Math.max(
        Number(drug?.quantity ?? 0) -
          (source === 'execution' || drug?.__packageChild
            ? Number(drug?.refundNum ?? 0)
            : 0),
        0
      )
      if (drug?.isDeducted === false) {
        return `${totalCount}${drug?.unit || ''}`
      }
      return `${usedCount}/${totalCount}`
    }
    const getTreatmentItemAmount = (drug: any) =>
      getNumericValue(isCharged ? drug?.payPrice : drug?.totalPrice)
    const getTreatmentSingleQuantity = (drug: any) => {
      if (Object.prototype.hasOwnProperty.call(drug, 'executionQuantity')) {
        return drug.executionQuantity ?? ''
      }

      return drug.quantity ?? ''
    }
    const getTreatmentExecutionAmount = (drug: any) => {
      const singleQuantity = getNumericValue(getTreatmentSingleQuantity(drug))
      const unitPrice = getNumericValue(drug?.unitPrice)

      return singleQuantity * unitPrice
    }
    const getTreatmentAcupoints = (drug: any) => {
      return String(drug?.remark || '').trim()
    }
    const getDisplayText = (...values: any[]) => {
      for (const value of values) {
        if (value !== undefined && value !== null && String(value).trim()) {
          return String(value).trim()
        }
      }
      return ''
    }
    const treatmentDrugList = Array.isArray(detailData?.physioList)
      ? detailData.physioList
      : []
    if (output && !treatmentDrugList.length) return
    const packageDisplay = Number(config.packageDisplay)
    const isTreatmentPackageMain = (item: any) =>
      !item?.__packageChild &&
      Number(item?.serviceType ?? item?.category) === 3
    const treatmentPrintList = treatmentDrugList.flatMap((item: any) => {
      const serviceType = Number(item?.serviceType ?? item?.category)
      if (serviceType !== 3) return [item]

      const children = Array.isArray(item?.comboItemList)
        ? item.comboItemList.map((child: any) => ({
            ...child,
            __packageChild: true,
          }))
        : []
      if (packageDisplay === 2) return children
      if (packageDisplay === 3) return [item, ...children]
      return [item]
    })
    const firstTreatmentDrug = treatmentDrugList[0] || {}
    const getTreatmentFooterAmount = () => {
      return treatmentDrugList.reduce(
        (total: number, drug: any) =>
          total + getTreatmentItemAmount(drug),
        0
      )
    }
    const footerItems = [
      showFoot('开单人')
        ? {
            label: '开单人',
            value: getDisplayText(
              detailData?.openUserName,
              detailData?.prescriber,
              detailData?.billingUserName,
              detailData?.createUserName,
              detailData?.doctorName,
              firstTreatmentDrug?.openUserName,
              firstTreatmentDrug?.prescriber,
              firstTreatmentDrug?.billingUserName,
              firstTreatmentDrug?.createUserName,
              firstTreatmentDrug?.doctorName
            ),
          }
        : null,
      showFoot('医生')
        ? {
            label: '医生',
            value: getDisplayText(
              detailData?.doctorName,
              firstTreatmentDrug?.doctorName
            ),
          }
        : null,
      showFoot('金额')
        ? {
            label: '金额',
            value: getTreatmentFooterAmount().toFixed(2),
          }
        : null,
      showFoot('收费员')
        ? {
            label: '收费员',
            value: getDisplayText(
              detailData?.chargeUserName,
              detailData?.chargesUserName,
              detailData?.chargeName,
              detailData?.cashierUserName,
              detailData?.cashierName,
              detailData?.cashier,
              firstTreatmentDrug?.chargeUserName,
              firstTreatmentDrug?.chargesUserName,
              firstTreatmentDrug?.chargeName,
              firstTreatmentDrug?.cashierUserName,
              firstTreatmentDrug?.cashierName,
              firstTreatmentDrug?.cashier
            ),
          }
        : null,
      showFoot('打印时间')
        ? {
            label: '打印时间',
            value: custFormatDate(new Date(), 'YYYY-MM-DD HH:mm:ss'),
          }
        : null,
    ].filter(Boolean)
    const footerContentHtml = footerItems.length
      ? `<div style="display: flex; flex-wrap: wrap; justify-content: space-between; gap: ${
          config.lineSpacing || 5
        }px; border-top: 2px solid #000; padding-top: ${
          config.lineSpacing || 5
        }px; margin-bottom: ${config.lineSpacing || 5}px;">
          ${footerItems.filter((item: any) => item.label !== '打印时间')
            .map(
              (item: any) =>
                `<span style="white-space: nowrap; margin-right: ${
                  (config.lineSpacing || 5) * 2
                }px;">${item.label}：${escapeHtml(item.value)}</span>`
            )
            .join('')}
        </div>`
      : ''
    const getPatientMobileText = () => {
      if (!showField('手机号') || config.phoneDisplay == 3) return ''
      if (config.phoneDisplay == 2 && patientInfo.mobile) {
        return patientInfo.mobile.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
      }

      return patientInfo.mobile || ''
    }
    const headerInfoItems = [
      {
        text: (() => {
          // 固定显示姓名、性别、年龄
          const ageText = [
            patientInfo.ageYear ? `${patientInfo.ageYear}岁` : '',
            patientInfo.ageMonth ? `${patientInfo.ageMonth}月` : ''
          ].filter(Boolean).join('')
          const parts = [
            patientInfo.userName,
            patientInfo.sex,
            ageText
          ].filter(Boolean)
          return parts.length > 0 ? `姓名：${parts.join(' ')}` : ''
        })()
      },
      { text: `日期：${custFormatDate(detailData.createTime, 'YYYY-MM-DD')}` },
      {
        text:
          showField('诊号') && patientInfo.visitNumber
            ? `诊号：${patientInfo.visitNumber}`
            : '',
      },
      {
        text:
          showField('身份证') && patientInfo.cardNo
            ? `身份证：${patientInfo.cardNo}`
            : '',
      },
      { text: getPatientMobileText() ? `手机：${getPatientMobileText()}` : '' },
      {
        text:
          showField('科室') && detailData.departmentsName
            ? `科室：${detailData.departmentsName}`
            : '',
      },
      {
        text:
          showField('诊断') && detailData.diagnosis
            ? `诊断：${detailData.diagnosis}`
            : '',
        wrap: true,
      },
    ].filter(item => item.text)
    const headerInfoHtml = headerInfoItems
      .map(
        item =>
          `<span style="${
            item.wrap
              ? 'display: block; flex: 1 0 100%; max-width: 100%; white-space: normal; overflow-wrap: anywhere; word-break: break-all;'
              : 'display: inline-block; white-space: nowrap;'
          } margin-right: ${(config.lineSpacing || 5) * 2}px; margin-bottom: ${
            config.lineSpacing || 5
          }px;">${escapeHtml(item.text)}</span>`
      )
      .join('')
    const html = renderTreatmentDocument(config, {
      headerHtml: headerInfoHtml,
      footerHtml: footerContentHtml + `<div class="t-footer-note">${escapeHtml(config.footerRemark || '')}</div>` +
        (showFoot('打印时间') ? `<div class="t-print-time">打印时间：${custFormatDate(new Date(), 'YYYY-MM-DD HH:mm:ss')}</div>` : ''),
      rows: treatmentPrintList.map((drug: any) => ({
        name: drug.drugName || drug.itemName || '',
        child: drug.__packageChild,
        packageMain: isTreatmentPackageMain(drug),
        singleQuantity: isTreatmentPackageMain(drug) ? '-' : getTreatmentSingleQuantity(drug),
        unitPrice: drug.unitPrice == null ? '' : Number(drug.unitPrice).toFixed(2),
        progress: formatTreatmentProgress(drug),
        executionAmount: isTreatmentPackageMain(drug) ? '-' :
          drug.unitPrice == null || getTreatmentSingleQuantity(drug) === '' ? '' : getTreatmentExecutionAmount(drug).toFixed(2),
        totalAmount: (isCharged ? drug.payPrice : drug.totalPrice) == null ? '' : getTreatmentItemAmount(drug).toFixed(2),
        description: [
          drug.frequencyUnit,
          drug.days == null ? '' : `${drug.days}天`,
          showTreat('治疗单包含穴位') && getTreatmentAcupoints(drug) ? `穴位：${getTreatmentAcupoints(drug)}` : '',
          drug.executor ? `执行人：${drug.executor}` : '',
        ].filter(Boolean).join('，'),
      })),
    })

    return await executePrint(html, paperSize, '治疗理疗单', getHospitalName(config), false, undefined, output)
  } catch (error) {
    if (output) throw error
    console.error('打印失败:', error)
    ElMessage.error('打印失败')
  }
}

// 打印用药说明
export const printMedicationInstruction = async (
  medicalId: string | number,
  paidOnly = false,
  output?: PrintOutputOptions
) => {
  try {
    const [res, configRes] = await Promise.all([
      getMedicationInstructionPrint({ id: medicalId, paidOnly }),
      printPrescriptionDetail(),
    ])
    if (res.code !== 0 || !res.data) throw new Error(res.msg || '获取用药说明失败')
    if (configRes.code !== 0 || !configRes.data) throw new Error(configRes.msg || '获取用药说明打印配置失败')
    const config = configRes.data
    const patientData = res.data
    const paperSize = {
      width: 'auto',
      height: 'auto',
      margin: '0',
      maxWidth: '100%',
    }
    const baseFontSize = Math.max(config.fontSize || 12, 14)

    let html = `<div style="width: 100%; box-sizing: border-box; font-size: ${baseFontSize}px; line-height: ${
      (config.lineSpacing || 5) + 18
    }px;">`
    if (config.settingName)
      html += `<div style="text-align: center; font-weight: bold; font-size: ${
        baseFontSize + 4
      }px; margin-bottom: ${config.lineSpacing || 5}px;">${
        config.settingName
      }</div>`
    html += `<div style="text-align: center; font-size: ${
      baseFontSize + 2
    }px; margin-bottom: ${config.lineSpacing || 5}px;">用药说明</div>`
    let notesHtml = ''
    if (
      patientData.prescriptionList &&
      patientData.prescriptionList.length > 0
    ) {
      patientData.prescriptionList.forEach((prescription: any) => {
          if (hasMedicationNotes(prescription.medicationNotes)) {
            const removeLeadingPrescriptionName = (
              notes: string,
              prescriptionName?: string
            ) => {
              if (!notes || !prescriptionName) return notes
              const escapedName = prescriptionName.replace(
                /[.*+?^${}()|[\]\\]/g,
                '\\$&'
              )
              return notes.replace(
                new RegExp(
                  `^\\s*${escapedName}\\s*(<br\\s*\\/?>|\\r?\\n)?`,
                  'i'
                ),
                ''
              )
            }
            const sanitizedNotes = removeLeadingPrescriptionName(
              prescription.medicationNotes,
              prescription.name
            )
            notesHtml += `<div style="margin-bottom: ${
              config.lineSpacing || 5
            }px;">
                <span>${sanitizedNotes}</span>
                <div>${prescription.humanisticCare ?? ''}</div>
              </div>`
          }
      })
    }

    if (!notesHtml) return
    html += `<div style="margin-top: ${
      config.lineSpacing || 5
    }px;">${notesHtml}</div>`
    html += `<div style="margin-top: ${
      config.lineSpacing || 5
    }px; text-align: right; font-size: ${Math.max(
      baseFontSize - 2,
      12
    )}px; white-space: nowrap;">打印时间：${custFormatDate(
      new Date(),
      'YYYY-MM-DD HH:mm:ss'
    )}</div>`
    html += `<div style="margin-top: 30mm; border-bottom: 1px dashed #000; height: 0; width: 100%;"></div>`

    return await executePrint(html, paperSize, '用药说明', getHospitalName(config), true, undefined, output)
  } catch (error) {
    if (output) throw error
    console.error('打印失败:', error)
    ElMessage.error(getPrintFailureMessage(error))
  }
}

// 打印通用用药说明
export const printGeneralMedicationInstruction = async (
  instructionData: any
) => {
  try {
    const res = await printMedicalDetail()
    if (res.code !== 0 || !res.data) {
      ElMessage.error('获取打印配置失败')
      return
    }
    const config = res.data
    const paperSize = {
      width: 'auto',
      height: 'auto',
      margin: '0',
      maxWidth: '100%',
    }
    const baseFontSize = Math.max(config.fontSize || 14, 14)

    const html = `<div style="width: 100%; box-sizing: border-box; font-size: ${baseFontSize}px; line-height: ${
      (config.lineSpacing || 5) + 18
    }px; padding: 0; font-family: Arial, sans-serif;">
      ${
        config.hospitalName || config.settingName
          ? `<div style="text-align: center; font-weight: bold; font-size: ${
              baseFontSize + 4
            }px; margin-bottom: ${(config.lineSpacing || 5) + 10}px;">${
              config.hospitalName || config.settingName
            }</div>`
          : ''
      }
      ${
        instructionData.firstText
          ? `<div style="margin-bottom: ${
              (config.lineSpacing || 5) + 5
            }px; text-indent: 2em;">${instructionData.firstText}</div>`
          : ''
      }

      ${
        instructionData.brief
          ? `<div style="margin-bottom: ${
              (config.lineSpacing || 5) + 5
            }px;"><strong>服药说明：</strong>${instructionData.brief}</div>`
          : ''
      }

      ${
        instructionData.takeTime
          ? `<div style="margin-bottom: ${
              (config.lineSpacing || 5) + 5
            }px;"><strong>服药时间：</strong>${instructionData.takeTime}</div>`
          : ''
      }

      ${
        instructionData.avoidCertain
          ? `<div style="margin-bottom: ${
              (config.lineSpacing || 5) + 5
            }px;"><strong>服药期间忌口：</strong>${
              instructionData.avoidCertain
            }</div>`
          : ''
      }

      ${
        instructionData.taboo
          ? `<div style="margin-bottom: ${
              (config.lineSpacing || 5) + 5
            }px;"><strong>服药期间禁忌：</strong>${instructionData.taboo}</div>`
          : ''
      }

      ${
        instructionData.lastText
          ? `<div style="margin-bottom: ${
              (config.lineSpacing || 5) + 5
            }px; text-indent: 2em;">${instructionData.lastText}</div>`
          : ''
      }
      <div style="margin-top: ${
        config.lineSpacing || 5
      }px; text-align: right; font-size: ${Math.max(
      baseFontSize - 2,
      12
    )}px; white-space: nowrap;">打印时间：${custFormatDate(
      new Date(),
      'YYYY-MM-DD HH:mm:ss'
    )}</div>
      <div style="margin-top: 30mm; border-bottom: 1px dashed #000; height: 0; width: 100%;"></div>
    </div>`

    executePrint(html, paperSize, '用药说明', getHospitalName(config), true)
  } catch (error) {
    console.error('打印失败:', error)
    ElMessage.error('打印失败')
  }
}

const maskReceiptMiddleFour = (value: any) => {
  const text = String(value ?? '').trim()
  if (text.length < 7) return text
  return `${text.slice(0, 3)}****${text.slice(7)}`
}

const formatReceiptMoney = (value: any) => {
  const amount = Number(value)
  return Number.isFinite(amount) ? amount.toFixed(2) : ''
}

const createMemberRechargeReceipt = (data: any, config: any) => {
  const fontSize = Number(config.fontSize) || 12
  const lineHeight = Math.max(fontSize + 4, 18)
  const hospitalName = getHospitalName(config)
  const sex = data.sex == 1 ? '男' : data.sex == 2 ? '女' : ''
  const mobile = maskReceiptMiddleFour(data.mobile)
  const cardNo = maskReceiptMiddleFour(data.cardNo)
  const chargeTime = data.createTime
    ? custFormatDate(data.createTime, 'YYYY-MM-DD HH:mm:ss')
    : ''
  const memberBalance =
    hasNumericValue(data.balance) && hasNumericValue(data.giftBalance)
      ? formatReceiptMoney(
          getNumericValue(data.balance) + getNumericValue(data.giftBalance)
        )
      : ''
  const renderTextLine = (label: string, value: any) => {
    if (value === '' || value === null || value === undefined) return ''
    return `<div style="white-space: nowrap;"><strong>${label}：</strong>${escapeHtml(
      String(value)
    )}</div>`
  }
  const renderValueLine = (label: string, value: any) => {
    if (value === '' || value === null || value === undefined) return ''
    return `<div style="display: flex; justify-content: space-between; gap: 8px; white-space: nowrap;">
      <strong>${label}</strong>
      <span>${escapeHtml(String(value))}</span>
    </div>`
  }

  return `
    <div style="font-size: ${fontSize}px; line-height: ${lineHeight}px; padding: 0; font-family: Arial, sans-serif; box-sizing: border-box;">
      ${
        hospitalName
          ? `<div style="text-align: center; font-weight: bold; font-size: ${
              fontSize + 2
            }px;">${escapeHtml(hospitalName)}</div>`
          : ''
      }
      <div style="text-align: center; font-weight: 600; margin-bottom: 4px;">${escapeHtml(
        String(data.title ?? '会员充值凭证')
      )}</div>
      <div style="display: flex; justify-content: space-between; gap: 8px; white-space: nowrap;">
        <span><strong>姓名：</strong>${escapeHtml(
          String(data.patientName ?? '')
        )}</span>
        ${sex ? `<span><strong>性别：</strong>${sex}</span>` : ''}
      </div>
      ${renderTextLine('手机', mobile)}
      <div style="border-top: 1px dashed #000; margin: 1px 0;"></div>
      ${renderTextLine('会员类型', data.cardName)}
      ${renderTextLine('会员卡号', cardNo)}
      ${renderTextLine('充值金额', formatReceiptMoney(data.amount))}
      ${renderTextLine('赠送金额', formatReceiptMoney(data.giftAmount))}
      ${renderTextLine('充值地点', data.deptName)}
      <div style="border-top: 1px dashed #000; margin: 1px 0;"></div>
      ${renderValueLine(
        '实付',
        hasNumericValue(data.amount)
          ? `${
              data.payChannel ? `（${String(data.payChannel)}）` : ''
            }${formatReceiptMoney(data.amount)}`
          : ''
      )}
      ${renderValueLine('会员卡余额', memberBalance)}
      ${renderValueLine('收费员', data.operator)}
      ${renderValueLine('收费时间', chargeTime)}
    </div>`
}

// 打印会员/卡项凭证
export const printMemberReceipt = async (data: any) => {
  try {
    const res = await printRestoreDetail()
    if (res.code !== 0 || !res.data) {
      ElMessage.error('获取打印配置失败')
      return
    }
    const config = res.data
    const paperSize = getPaperSize({ paperSize: '80mm' })

    if (data.receiptType === 'memberRecharge') {
      const html = createMemberRechargeReceipt(data, config)
      executePrint(html, paperSize, data.title, getHospitalName(config))
      return
    }

    let html = `
    <div style="font-size: ${config.fontSize || 12}px; line-height: ${
      (config.lineSpacing || 5) + 15
    }px; padding: 0; padding-bottom: 0; font-family: Arial, sans-serif; box-sizing: border-box;">
    `

    // 抬头
    if (config.settingName) {
      html += `<div style="text-align: center; font-weight: bold; font-size: ${
        (config.fontSize || 12) + 2
      }px; margin-bottom: ${config.lineSpacing || 5}px;">${
        config.settingName
      }</div>`
    }

    html += `<div style="text-align: center; margin-bottom: ${
      config.lineSpacing || 5
    }px;">${data.title || '收费凭证'}</div>`

    // 患者信息
    html += `<div style="margin-bottom: ${config.lineSpacing || 5}px;">`
    if (data.patientName) {
      html += `姓名：${data.patientName} `
    }
    if (data.sex !== undefined) {
      html += `${data.sex == 1 ? '男' : data.sex == 2 ? '女' : ''} `
    }
    if (data.age) {
      html += `${data.age} `
    }
    html += `</div>`

    if (data.mobile) {
      let phone = data.mobile
      if (config.phoneDisplay === 2) {
        phone = phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
      }
      html += `<div style="margin-bottom: ${
        config.lineSpacing || 5
      }px;">手机：${phone}</div>`
    }

    // 业务信息
    html += `<div style="border-top: 1px dashed #000; margin: ${
      config.lineSpacing || 5
    }px 0; padding-top: ${config.lineSpacing || 5}px;">`

    if (data.cardName) {
      html += `<div style="margin-bottom: ${config.lineSpacing || 5}px;">${
        data.cardNameLabel || '项目'
      }：${data.cardName}</div>`
    }

    if (data.content) {
      html += `<div style="margin-bottom: ${config.lineSpacing || 5}px;">${
        data.contentLabel || '内容'
      }：${data.content}</div>`
    }

    if (data.amount !== undefined) {
      html += `<div style="display: flex; justify-content: space-between; margin-bottom: ${
        config.lineSpacing || 5
      }px;">
        <span>${data.amountLabel || '金额'}</span>
        <span>￥${formatMoney(data.amount)}</span>
      </div>`
    }

    if (data.giftAmount !== undefined && data.giftAmount > 0) {
      html += `<div style="display: flex; justify-content: space-between; margin-bottom: ${
        config.lineSpacing || 5
      }px;">
        <span>赠送金额</span>
        <span>￥${formatMoney(data.giftAmount)}</span>
      </div>`
    }

    if (data.balance !== undefined) {
      html += `<div style="display: flex; justify-content: space-between; margin-bottom: ${
        config.lineSpacing || 5
      }px;">
        <span>当前余额</span>
        <span>￥${formatMoney(getMemberBalance(data))}</span>
      </div>`
    }

    html += `</div>`

    // 支付信息
    html += `<div style="border-top: 1px dashed #000; margin: ${
      config.lineSpacing || 5
    }px 0; padding-top: ${config.lineSpacing || 5}px;">`

    if (data.payChannel) {
      html += `<div style="margin-bottom: ${
        config.lineSpacing || 5
      }px;">支付方式：${data.payChannel}</div>`
    }
    if (data.remark) {
      html += `<div style="margin-bottom: ${
        config.lineSpacing || 5
      }px;">备注：${data.remark}</div>`
    }

    const now = new Date()
    const currentTime = now.toLocaleString('zh-CN')
    html += `<div style="margin-bottom: ${
      config.lineSpacing || 5
    }px;">打印时间：${currentTime}</div>`

    if (data.operator) {
      html += `<div style="margin-bottom: ${
        config.lineSpacing || 5
      }px;">操作员：${data.operator}</div>`
    }

    html += `</div>`
    html += `<div class="bottom-space"></div>`
    html += `</div>`

    executePrint(html, paperSize, data.title || '凭证', getHospitalName(config))
  } catch (error) {
    console.error('打印失败:', error)
    ElMessage.error('打印失败')
  }
}

// 打印患者标签
export const printPatientLabelByData = async (
  patientData: any,
  source = 'medical',
  output?: PrintOutputOptions
) => {
  try {
    const [res, configRes] = await Promise.all([
      getPatientLabelPrint({ id: patientData?.id, source }), printPatientDetail(),
    ])
    if (res.code !== 0 || !res.data) throw new Error(res.msg || '获取患者标签失败')
    if (configRes.code !== 0 || !configRes.data) throw new Error(configRes.msg || '获取患者标签打印配置失败')
    patientData = res.data
    const patientConfig = configRes.data
    const config = {
      ...patientConfig,
      patientInfo: normalizePrintSelection(patientConfig.patientInfo),
      phoneDisplay: [1, 2, 3].includes(patientConfig.phoneDisplay)
        ? patientConfig.phoneDisplay
        : 2,
      fontSize: Number(patientConfig.fontSize) || 14,
      lineSpacing: Number(patientConfig.lineSpacing) || 10,
    }

    patientData = patientData || {}
    const checkField = (key: string) => config.patientInfo?.includes(key)
    const hasContent = (value: any) =>
      value !== undefined && value !== null && String(value).trim() !== ''
    const formatSex = (value: any) =>
      value == 1 ? '男' : value == 2 ? '女' : ''
    const formatAge = () =>
      [
        hasContent(patientData.ageYear) ? `${patientData.ageYear} 岁` : '',
        hasContent(patientData.ageMonth) ? `${patientData.ageMonth} 月` : '',
        hasContent(patientData.ageDay) ? `${patientData.ageDay} 天` : '',
      ]
        .filter(Boolean)
        .join(' ')
    const formatBirthday = () => {
      const birthday = patientData.birthDate || patientData.birthday
      if (Array.isArray(birthday)) return birthday.filter(Boolean).join('-')
      return birthday || ''
    }
    const formatPhone = (phone: any) => {
      if (!phone || config.phoneDisplay === 3) return ''
      const phoneText = String(phone)
      if (config.phoneDisplay === 2) {
        return phoneText.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
      }
      return phoneText
    }

    const infoRows = [
      {
        label: '手机',
        value: checkField('手机') ? formatPhone(patientData.mobile) : '',
      },
      { label: '生日', value: checkField('生日') ? formatBirthday() : '' },
      {
        label: '诊号',
        value: checkField('诊号')
          ? getPatientVisitNumber(patientData) || '-'
          : '',
      },
      {
        label: '医生',
        value: checkField('医生') ? patientData.doctorName || '' : '',
      },
      {
        label: '打印时间',
        value: checkField('打印时间')
          ? custFormatDate(new Date(), 'YYYY-MM-DD HH:mm:ss')
          : '',
      },
    ].filter(item => hasContent(item.value))

    const paperSize = getPaperSize({ paperSize: '80mm' })
    const settingName = getHospitalName(config)
    const fontSize = config.fontSize
    const lineSpacing = config.lineSpacing
    const lineHeight = fontSize + lineSpacing
    const settingNameFontSize = fontSize + 14
    const nameFontSize = fontSize + 16
    const metaFontSize = fontSize + 6
    const sexText = checkField('性别') ? formatSex(patientData.sex) : ''
    const ageText = checkField('年龄') ? formatAge() : ''
    const metaItems = [sexText, ageText].filter(Boolean)

    let html = `
      <div style="width: 360px; box-sizing: border-box; padding: 18px 16px; font-family: Arial, sans-serif; color: #222; font-size: ${fontSize}px; line-height: ${lineHeight}px; word-break: break-all;">
        ${
          settingName
            ? `<div style="font-size: ${settingNameFontSize}px; line-height: ${
                settingNameFontSize + lineSpacing
              }px; margin-bottom: ${lineSpacing + 4}px;">${escapeHtml(
                settingName
              )}</div>`
            : ''
        }
        <div style="display: flex; align-items: baseline; gap: ${lineSpacing}px; margin-bottom: ${lineSpacing}px; white-space: nowrap;">
          <span style="font-size: ${nameFontSize}px; line-height: ${
      nameFontSize + lineSpacing
    }px; font-weight: 700;">${escapeHtml(
      patientData.userName || patientData.name || ''
    )}</span>
          ${metaItems
            .map(
              item =>
                `<span style="font-size: ${metaFontSize}px; line-height: ${
                  metaFontSize + lineSpacing
                }px;">${escapeHtml(item)}</span>`
            )
            .join('')}
        </div>
        ${infoRows
          .map(
            item =>
              `<div style="font-size: ${fontSize}px; line-height: ${lineHeight}px; margin-top: ${
                item.label === '打印时间'
                  ? lineSpacing
                  : Math.max(Math.round(lineSpacing / 2), 1)
              }px;">${escapeHtml(item.label)}：${escapeHtml(item.value)}</div>`
          )
          .join('')}
      </div>
    `
    return await executePrint(html, paperSize, '患者标签', settingName, false, undefined, output)
  } catch (error) {
    if (output) throw error
    console.error('打印患者标签失败:', error)
    ElMessage.error(getPrintFailureMessage(error))
  }
}

export const printPatientLabel = async (
  medicalId: string | number,
  printConfigParams?: PrintConfigParams,
  output?: PrintOutputOptions
) => {
  try {
    return await printPatientLabelByData({ id: medicalId }, 'medical', output)
  } catch (error) {
    if (output) throw error
    console.error('打印患者标签失败:', error)
    ElMessage.error(getPrintFailureMessage(error))
  }
}

// 打印用药标签
export const printMedicationLabel = async (
  medicalId: string | number,
  printConfigParams?: PrintConfigParams,
  output?: PrintOutputOptions
) => {
  try {
    const [res, configRes] = await Promise.all([
      getMedicationLabelPrint({ id: medicalId }), printDrugDetail(),
    ])
    if (res.code !== 0 || !res.data) throw new Error(res.msg || '获取用药标签失败')
    if (configRes.code !== 0 || !configRes.data) throw new Error(configRes.msg || '获取用药标签打印配置失败')
    const drugConfig = configRes.data
    const patientData = res.data
    let config = {
      patientInfo: drugConfig.patientInfo || [],
      phoneDisplay: drugConfig.phoneDisplay ?? 2,
      visitInfo: drugConfig.medicalInfo || [],
      westernMedicine: drugConfig.westernMedicine || [],
      chineseMedicine: drugConfig.chineseMedicine || [],
      chineseMedicineRemark: drugConfig.chineseRemark || '',
      footerContent: drugConfig.footerContent || [],
      lineSpacing: Number(drugConfig.lineSpacing) || 10,
      fontSize: Number(drugConfig.fontSize) || 14,
    }

    // 用药标签一般使用 80mm 或者 58mm 的标签纸
    const paperSize = getPaperSize({ paperSize: '80mm' })

    const checkPat = (key: string) => config.patientInfo?.includes(key)
    const checkVisit = (key: string) => config.visitInfo?.includes(key)
    const checkXi = (key: string) => config.westernMedicine?.includes(key)
    const checkChin = (key: string) => config.chineseMedicine?.includes(key)
    const checkFoot = (key: string) => config.footerContent?.includes(key)

    const fontSize = config.fontSize || 14
    const lineSpacing = config.lineSpacing || 5
    const lineHeight = fontSize + lineSpacing

    const baseStyle = `font-size: ${fontSize}px; line-height: ${lineHeight}px; font-family: Arial, sans-serif; box-sizing: border-box; width: 100%;`
    const marginStyle = `margin-bottom: ${lineSpacing}px;`
    const borderStyle = `border-bottom: 2px solid #000; padding-bottom: ${lineSpacing}px; margin-bottom: ${lineSpacing}px;`
    const flexBetween = `display: flex; justify-content: space-between; align-items: center;`

    let html = `<div style="${baseStyle}">`

    const westernList: any[] = []
    const chineseList: any[] = []

    if (patientData.prescriptionList) {
      patientData.prescriptionList.forEach((p: any) => {
        if (p.prescriptionType === 1) chineseList.push(p)
        else if (p.prescriptionType === 2) westernList.push(p)
      })
    }

    if (westernList.length === 0 && chineseList.length === 0) {
      ElMessage.warning('该患者没有成药或中药处方，无法打印用药标签')
      return
    }

    // 格式化手机号
    let phone = patientData.mobile || ''
    if (checkPat('手机')) {
      if (config.phoneDisplay === 2) {
        phone = phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
      }
    } else {
      phone = ''
    }

    // 生成标签 HTML 的通用函数
    const generateLabelHtml = (
      type: 'western' | 'chinese',
      itemData: any,
      parentData: any = null,
      isLast: boolean = true
    ) => {
      // 每个标签独占一页，非最后一个标签后强制分页，保证逐张打印
      const pageBreakStyle = isLast
        ? ''
        : 'page-break-after: always; break-after: page;'
      let labelHtml = `<div style="break-inside: avoid; page-break-inside: avoid; padding: 10px 0; border-bottom: 1px dashed #ccc; margin-bottom: 15px; ${pageBreakStyle}">`

      // 1. 头部信息
      labelHtml += `<div style="${borderStyle} ${flexBetween} flex-wrap: wrap;">`
      labelHtml += `<span style="font-weight: bold; font-size: ${
        fontSize + 2
      }px;">${patientData.userName || ''}</span>`
      labelHtml += `<span>${
        patientData.sex === 1 ? '男' : patientData.sex === 2 ? '女' : ''
      } ${patientData.ageYear ? patientData.ageYear + '岁' : ''}</span>`
      if (checkPat('诊号') && getPatientVisitNumber(patientData)) {
        labelHtml += `<span>诊号：${getPatientVisitNumber(patientData)}</span>`
      }
      if (phone) labelHtml += `<span>${phone}</span>`
      if (checkVisit('开单科室') && patientData.departmentsName) {
        labelHtml += `<span>${patientData.departmentsName}</span>`
      }
      labelHtml += `</div>`

      // 2. 药品明细
      labelHtml += `<div style="${borderStyle} min-height: 100px;">`
      if (type === 'western') {
        labelHtml += `<div style="font-weight: bold; ${marginStyle}">${
          itemData.drugName || ''
        }</div>`
        labelHtml += `<div style="${marginStyle}">规格：${
          itemData.specification || itemData.spec || ''
        } <span style="margin-left: 10px;">${
          checkXi('总量')
            ? '×' + (itemData.quantity ?? '') + (itemData.unit || '')
            : ''
        }</span></div>`

        labelHtml += `<div style="display: flex;">`
        labelHtml += `<span style="white-space: nowrap;">用法：</span>`
        labelHtml += `<div style="display: flex; flex-direction: column; font-weight: bold;">`
        labelHtml += `<span>${itemData.dosage || ''} ${
          itemData.usageMethod || ''
        }</span>`
        labelHtml += `<span>${
          itemData.frequency || itemData.frequencyUnit || ''
        } ${
          checkXi('天数') && itemData.days ? '共' + itemData.days + '天' : ''
        }</span>`
        labelHtml += `</div></div>`

        if (checkXi('皮试') && itemData.skinTest) {
          labelHtml += `<div style="margin-top: 5px;">皮试：${itemData.skinTest}</div>`
        }
      } else if (type === 'chinese') {
        // 中药标签只显示处方名称（如：中药处方一），不显示药品明细
        const prescriptionName = escapeHtml(
          String(
            parentData?.name || parentData?.prescriptionName || '中药处方'
          ).trim()
        )
        labelHtml += `<div style="font-weight: bold; ${marginStyle}">${prescriptionName} 共${
          parentData?.quantity ?? ''
        }剂</div>`

        labelHtml += `<div style="display: flex;">`
        labelHtml += `<span style="white-space: nowrap;">用法：</span>`
        labelHtml += `<div style="display: flex; flex-direction: column; font-weight: bold;">`
        labelHtml += `<span>${
          checkChin('煎法') ? parentData?.usageMethod || '' : ''
        } ${parentData?.dosage || ''}</span>`
        labelHtml += `<span>${joinDisplayParts([
          parentData?.frequency,
          parentData?.frequencyUnit,
        ])}</span>`
        labelHtml += `</div></div>`

        if (checkChin('处方备注') && parentData?.remark) {
          labelHtml += `<div style="margin-top: 5px;">备注：${parentData.remark}</div>`
        }
      }
      labelHtml += `</div>`

      // 3. 底部信息
      labelHtml += `<div style="${flexBetween} font-size: ${fontSize - 2}px;">`
      let remark = ''
      if (type === 'western' && checkXi('处方备注')) {
        remark = parentData?.remark ? '备注：' + parentData.remark : ''
      } else if (type === 'chinese' && checkChin('自定义备注')) {
        remark = config.chineseMedicineRemark || ''
      }
      labelHtml += `<span>${remark}</span>`

      if (checkFoot('打印时间')) {
        labelHtml += `<span>${custFormatDate(
          new Date(),
          'YYYY-MM-DD HH:mm:ss'
        )}</span>`
      }
      labelHtml += `</div>`

      labelHtml += `</div>`
      return labelHtml
    }

    const labelItems: Array<{
      type: 'western' | 'chinese'
      itemData: any
      parentData: any
    }> = []

    // 生成成药标签数据（成药处方内每个药品打印一个标签）
    westernList.forEach((prescription: any) => {
      if (prescription.drugList) {
        prescription.drugList.forEach((drug: any) => {
          labelItems.push({
            type: 'western',
            itemData: drug,
            parentData: prescription,
          })
        })
      }
    })

    // 生成中药标签数据（每个处方打印一个标签）
    chineseList.forEach((prescription: any) => {
      labelItems.push({
        type: 'chinese',
        itemData: {},
        parentData: prescription,
      })
    })

    labelItems.forEach((labelItem, index) => {
      html += generateLabelHtml(
        labelItem.type,
        labelItem.itemData,
        labelItem.parentData,
        index === labelItems.length - 1
      )
    })

    html += `</div>`
    html += `<div class="bottom-space"></div>`

    return await executePrint(html, paperSize, '用药标签', getHospitalName(config), false, undefined, output)
  } catch (error) {
    if (output) throw error
    console.error('打印标签失败:', error)
    ElMessage.error(getPrintFailureMessage(error))
  }
}
